import React, { useEffect, useMemo, useRef, useState, useLayoutEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { SuggestionCard, AppliedBlock } from "../components/AgentOutputScreen";
import {
  Sparkle,
  CircleNotch,
  Eraser,
  Copy as CopyIcon,
  DownloadSimple,
  CheckCircle,
  PaperPlaneTilt,
  ArrowLeft,
  Paperclip,
  X as XIcon,
  Image as ImageIcon,
  Gift,
  Rocket,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { getActiveAlap } from "../lib/activeAlap";
import { getActivePost, setActivePost, clearActivePost } from "../lib/activePost";
import OverwriteWarningDialog from "../components/OverwriteWarningDialog";

const OVERWRITE_PREF_KEY = "posztVarazslo.overwriteWarningDisabled";

const TEAL = "#75dff5";
const TEAL_BG = "rgba(117,223,245,0.06)";
const TEAL_BG_ACTIVE = "rgba(117,223,245,0.16)";
const TEAL_BORDER = "rgba(117,223,245,0.22)";
const TEAL_BORDER_ACTIVE = "rgba(117,223,245,0.55)";

const PURPLE_ACCENT = "#7c6cf2";

// Client-side defensive strip: occasionally the LLM appends a source tag
// like " · ajanlat" / " · avatar" / " · metafora" to the topic chip text.
// The source label is for backend slot logic, never for the user UI.
const TOPIC_SOURCE_SUFFIX = /\s*[·•\-:|]\s*(avatar(?:[\s\-_]?(?:fájdalom|fajdalom|vágy|vagy|napi[\s\-_]?jelenet))?|aj[áa]nlat(?:[\s\-_]?(?:módszertan|modszertan|b[óo]nusz))?|metafora|kultur[áa]lis|napi[\s\-_]?jelenet|forr[áa]s\s*:.*)\s*\.?\s*$/i;
function cleanTopic(t) {
  return typeof t === "string" ? t.replace(TOPIC_SOURCE_SUFFIX, "").trim() : t;
}

const CTA_OPTIONS_DEFAULT = [
  { id: "beszelgetes", title: "Beszélgetésindító", desc: "Kérdéssel zár, commentre / privát üzenetre hív." },
  { id: "ingyenes", title: "Ingyenes tartalom", desc: "Ingyenes tartalomra vagy első lépésre invitál." },
  { id: "jelentkezes", title: "Jelentkezés ajánlatra", desc: "Meghívás a fő ajánlatodra vagy egyik programodra." },
];

// Reels-en eltérő interakciós mechanizmusok érvényesülnek (komment +
// mentés + megosztás + privát üzenet kulcsszóval + bio-link), ezért a
// magyarázó leírások Reels-specifikusak. A CTA-NEVEK mindenhol azonosak.
const CTA_OPTIONS_REELS = [
  { id: "beszelgetes", title: "Beszélgetésindító", desc: "Kommentre, mentésre vagy megosztásra ösztönöz." },
  { id: "ingyenes", title: "Ingyenes tartalom", desc: "Kulcsszavas felhívás privát üzenetre." },
  { id: "jelentkezes", title: "Jelentkezés ajánlatra", desc: "Bio-linkre irányítja a nézőt." },
];

// LinkedIn-specifikus al-szövegek — szakmai környezet, profil-link
// helyett bio-link, kommentre szakmai válasz.
const CTA_OPTIONS_LINKEDIN = [
  { id: "beszelgetes", title: "Beszélgetésindító", desc: "Kommentre vagy szakmai válaszra hív." },
  { id: "ingyenes", title: "Ingyenes tartalom", desc: "Anyag-átadás profilon, linken vagy privát üzenetben." },
  { id: "jelentkezes", title: "Jelentkezés ajánlatra", desc: "Profil-linkre vagy privát üzenetre hív." },
];

// Carousel ugyanaz mint a Reels — mindkettő Instagram, ugyanazok a
// mechanizmusok (komment, mentés, megosztás, kulcsszavas privát
// üzenet, bio-link).
const CTA_OPTIONS_CAROUSEL = CTA_OPTIONS_REELS;

// A meta-sorban (TÉMA · NÉZŐPONT · CTA) a CTA címkét TELJES nevével
// jelenítjük meg minden platformon (FB / LinkedIn / Reels / Carousel),
// ahogy a Writer kiküldte. A korábbi rövidítés (BESZÉLGETÉS / INGYENES /
// AJÁNLAT) megszűnt — a teljes név sokkal egyértelműbb és magabiztosabb
// vizuálisan. A mentett JSON-ben mindig a teljes név szerepelt.
const META_CTA_SHORT = {
  "Beszélgetésindító": "Beszélgetésindító",
  "Ingyenes tartalom": "Ingyenes tartalom",
  "Jelentkezés ajánlatra": "Jelentkezés ajánlatra",
};
function shortCta(label) {
  if (!label) return "";
  return META_CTA_SHORT[label] || label;
}

// Quick-action refine chips that appear when user selects text on the cream
// post card. 6 Quick Buttons — Élesebb, Lágyabb, Konkrétabb, Több sztori,
// Humorosabb, Lepj meg! A `id` mező a backend QUICK_BUTTON_INSTRUCTIONS
// dict-jébe kapcsolódik (single source of truth a szerverben).
const QUICK_REFINE_ACTIONS = [
  { id: "edge",     label: "Élesebb",    instruction: "Erősebb érzelmi feszültség, mélyebb fájdalom, konkrétabb jelenet, nagyobb hatás." },
  { id: "soft",     label: "Lágyabb",    instruction: "Melegebb, empatikusabb, barátságosabb, több megértés." },
  { id: "concrete", label: "Konkrétabb", instruction: "Több konkrét részlet, időpont, hely, tárgy, érzés — semmi általánosítás." },
  { id: "story",    label: "Több sztori", instruction: "Cseréld ki vagy bővítsd ki egy rövid, életszagú személyes vagy hipotetikus jelenettel." },
  { id: "humor",    label: "Humorosabb", instruction: "Adj bele finom öniróniát vagy könnyed humort, de maradj empatikus." },
  { id: "surprise", label: "Lepj meg!",  instruction: "Legyél kreatívabb, váratlanabb, erősebb hook vagy fordulat ebben a részben." },
];

/**
 * MagnetPostFacebook — Facebook + LinkedIn sub-card flow, F3-identical shell.
 *
 * Platform-aware: a `platform` prop ("facebook" | "linkedin") parameterizes
 * the API base path, the page title, and the eyebrow strip. Everything
 * else (UI, components, layout, finomítás, mentés, post_title) IDENTIKUS.
 *
 * Takes over the entire main content area. Renders its own F3-style header
 * (back link + agent badge + segmented Beállítás/Poszt tabs) and a 2-column
 * body that mirrors AgentInputScreen (input phase) or AgentOutputScreen
 * (output phase).
 */
export default function MagnetPostFacebook({ onClose, onSwitchPlatform, platform = "facebook", inlineHost = false, onPhaseChange, webinarOverride = null }) {
  // Platform-aware copy + API paths. Minden API hívás `/attraction/post-${platform}/...`.
  const isReels = platform === "instagram_reels";
  const isCarousel = platform === "carousel";
  const PLATFORM_LABEL = isReels ? "IG REELS" : (isCarousel ? "CAROUSEL" : (platform === "linkedin" ? "LINKEDIN" : "FACEBOOK"));
  const PLATFORM_TITLE = isReels ? "Instagram Reels" : (isCarousel ? "Instagram Carousel" : (platform === "linkedin" ? "LinkedIn poszt" : "Facebook poszt"));
  // API slug eltér a platform azonosítótól: az IG Reels backend route
  // `/api/attraction/post-reels/...`, nem `post-instagram_reels`. Carousel
  // ugyanaz mint a platform név.
  const apiSlug = isReels ? "reels" : platform;
  const apiPath = (suffix) => `/attraction/post-${apiSlug}${suffix}`;
  // Reels-specifikus state: a strukturált 9-mezős content
  const [reelsContent, setReelsContent] = useState(null);
  // Carousel-specifikus state: a dia-alapú strukturált content
  const [carouselContent, setCarouselContent] = useState(null);

  const [phase, setPhase] = useState("input"); // "input" | "output"

  // Phase-szinkronizálás a parent-be (inline-host módban a parent az ennek
  // megfelelően vált opening-grid vs. full-shell layout között).
  //
  // 2 érték: "inline" | "full".
  //   • "inline"  → a wrapper CSAK az inline InputView-t adja vissza (a
  //                 parent kártya-grid látszik fölötte). Csak akkor, ha
  //                 még nincs vázlat és nincs kész poszt.
  //   • "full"    → a wrapper a teljes PostHeader + Beállítás/Poszt tab +
  //                 AnimatePresence shell-jét adja vissza (parent kártya-
  //                 grid `display: none`). Bármilyen output-állapot, vagy
  //                 ha a felhasználó a "Beállítás" tabra visszamegy egy
  //                 már létező eredmény mellett.

  const [tema, setTema] = useState("");
  const [ctaId, setCtaId] = useState(null);
  const [selectedTopicChip, setSelectedTopicChip] = useState(null);
  // selectedTopicObject: the full 14-field topic object behind the chip
  // (set when the user clicks a chip). Passed to /sketches so the Outline
  // Generator v2 has the full context. NULL when the user typed the topic
  // manually — backend then synthesises a minimal object.
  const [selectedTopicObject, setSelectedTopicObject] = useState(null);
  const [topics, setTopics] = useState([]);         // string[] for chip labels
  const [topicObjects, setTopicObjects] = useState([]); // dict[] full objects
  const [topicsLoading, setTopicsLoading] = useState(false);
  const [topicsRegenerating, setTopicsRegenerating] = useState(false);
  const [seenTopics, setSeenTopics] = useState([]); // accumulated exclusion list (strings)

  const activeAlap = useMemo(() => getActiveAlap(), []);
  const [aktivPoszt, setAktivPosztState] = useState(getActivePost());

  const [generating, setGenerating] = useState(false);
  const [vazlatok, setVazlatok] = useState([]);
  const [chosenSketch, setChosenSketch] = useState(null);
  const [fullPost, setFullPost] = useState("");
  const [postTitle, setPostTitle] = useState("");      // belső H1 cím (Tudástár dokumentum-név)
  const [postFirstLine, setPostFirstLine] = useState(""); // lockolt első sor (mentésnél küldjük)
  const [postMeta, setPostMeta] = useState(null); // {tema, szog, cta_tipus, forras, avatar_neve, ajanlat_neve, datum}
  const [expanding, setExpanding] = useState(false);
  // Track which Hook-Bank category IDs the current sketch batch used, so
  // when the user re-rolls sketches from chat we can exclude them.
  const [lastSketchHookCats, setLastSketchHookCats] = useState([]);

  // Output-phase F3-style refine state machine.
  //   refinePhase: "idle" | "loading" | "suggestion" | "applied"
  //
  //   - idle:       no active refinement
  //   - loading:    request in flight ("Finomítás folyamatban…")
  //   - suggestion: backend returned a proposed change → show SuggestionCard
  //                 with [Alkalmazom] [Új verziót] [Mégsem]
  //   - applied:    user accepted; brief "Frissítve. Visszavonás" affordance
  //
  // We keep `chatHistory` for a transcript-style log of accepted instructions,
  // but the live preview (suggestion + apply + undo) is the primary UX, F3-identical.
  const [refinePhase, setRefinePhase] = useState("idle");
  const [suggestion, setSuggestion] = useState(null); // { mod, snippet, teljes, instr, quote }
  const [suggestionError, setSuggestionError] = useState(false);
  const [regenerating, setRegenerating] = useState(false);
  const [undoSnapshot, setUndoSnapshot] = useState(null); // previous fullPost
  const undoTimerRef = useRef(null);

  // Browser selection state — captured live from cream-card mouseup.
  const [selection, setSelection] = useState(null);
  const [chatInput, setChatInput] = useState("");
  const [saved, setSaved] = useState(false);
  const [savingVault, setSavingVault] = useState(false);
  const [copied, setCopied] = useState(false);

  // Overwrite-warning state. The dialog only shows when there is already a
  // generated post AND the user has not opted-out via preferences.
  const [overwriteDialogOpen, setOverwriteDialogOpen] = useState(false);
  const [overwriteWarningDisabled, setOverwriteWarningDisabled] = useState(false);

  // ── displayMode (inline-host módban: "inline" | "full") ────────────
  // Inline-only mód CSAK akkor, ha még nincs vázlat és nincs kész poszt.
  // Bármilyen eredmény után (vagy phase=output) a teljes shell-t kell
  // renderelni (PostHeader + Beállítás/Poszt tabok látszanak felül).
  const hasResult = vazlatok.length > 0 || !!fullPost;
  const inlineHostDisplayMode =
    inlineHost && phase === "input" && !hasResult ? "inline" : "full";

  // Szinkronizálás a parent-be (csak inline-host módban érdemi).
  useEffect(() => {
    if (typeof onPhaseChange === "function") {
      onPhaseChange(inlineHostDisplayMode);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inlineHostDisplayMode]);


  // Hydrate the disable-forever pref on mount. Backend is the source of truth;
  // we don't block generation if the request fails — just default to "enabled".
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get("/user/preferences");
        const prefs = res.data?.preferences || {};
        if (!cancelled) setOverwriteWarningDisabled(!!prefs[OVERWRITE_PREF_KEY]);
      } catch { /* keep default */ }
    })();
    return () => { cancelled = true; };
  }, []);

  const previewRef = useRef(null);

  // Load 6 personalized topic chips on mount. Akkor is hívjuk, ha még
  // nincs explicit Aktív alap választva — a backend automatikusan a
  // legutóbbi mentésre fall-backel. (2026-05-26 fix: korábban a feltétel
  // miatt az egész lépés kimaradt és „eltűntek a téma-pillék" hatást
  // keltett, ha a user nem konfigurálta a Sidebar Aktív alap selector-t.)
  useEffect(() => {
    // webinarOverride mód: a webinar wrapper saját generate endpointot hív,
    // a Carousel /topics endpoint NEM releváns — egyszerűen kihagyjuk.
    if (webinarOverride) {
      setTopicsLoading(false);
      return;
    }
    let alive = true;
    setTopicsLoading(true);
    (async () => {
      try {
        const res = await api.post(apiPath("/topics"), {
          voice_id: activeAlap.voice || null,
          client_id: activeAlap.client || null,
          offer_id: activeAlap.offer || null,
        });
        const tt = Array.isArray(res.data?.topics) ? res.data.topics : [];
        const oo = Array.isArray(res.data?.topic_objects) ? res.data.topic_objects : [];
        if (alive) {
          setTopics(tt);
          setTopicObjects(oo);
          setSeenTopics(tt); // initialize exclusion baseline (visible titles)
        }
      } catch {
        if (alive) { setTopics([]); setTopicObjects([]); }
      } finally {
        if (alive) setTopicsLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [activeAlap, webinarOverride]);

  // ─── webinarOverride mount-effect ──────────────────────────────────────
  // Ha a webinar wrapper átadta a már LEGENERÁLT carousel_content-et a
  // `prefilledOutput`-on keresztül, akkor egyenesen az output fázisra
  // ugrunk, töltjük az állapotot, és a topics/sketches fázist kihagyjuk.
  // Default-undefined módban ez a useEffect no-op → a Carousel és a
  // MagnetPost flow bit-azonos marad.
  useEffect(() => {
    if (!webinarOverride || !webinarOverride.prefilledOutput) return;
    const out = webinarOverride.prefilledOutput;
    if (out.teljes_poszt) setFullPost(out.teljes_poszt);
    if (out.carousel_content && typeof out.carousel_content === "object") {
      setCarouselContent(out.carousel_content);
    }
    if (out.post_title) setPostTitle(out.post_title);
    if (out.post_first_line) setPostFirstLine(out.post_first_line);
    if (out.postMeta) setPostMeta(out.postMeta);
    if (out.tema) setTema(out.tema);
    setPhase("output");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);


  // Re-roll: ask backend for 6 NEW topics excluding everything we've seen.
  const regenerateTopics = async () => {
    if (topicsRegenerating || topicsLoading) return;
    setTopicsRegenerating(true);
    try {
      const res = await api.post(apiPath("/topics/regenerate"), {
        voice_id: activeAlap.voice,
        client_id: activeAlap.client,
        offer_id: activeAlap.offer,
        previous_topics: seenTopics,
      });
      const tt = Array.isArray(res.data?.topics) ? res.data.topics : [];
      const oo = Array.isArray(res.data?.topic_objects) ? res.data.topic_objects : [];
      if (tt.length) {
        setTopics(tt);
        setTopicObjects(oo);
        setSeenTopics((prev) => Array.from(new Set([...(prev || []), ...tt])));
        setSelectedTopicChip(null);
        setSelectedTopicObject(null);
      } else {
        toast.error("Nem érkezett új téma. Próbáld újra.");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Új témák generálása sikertelen.");
    } finally {
      setTopicsRegenerating(false);
    }
  };

  // Capture text selection inside the cream card preview.
  useEffect(() => {
    if (phase !== "output" || !fullPost) return;
    const handleMouseUp = () => {
      const sel = window.getSelection();
      if (!sel || sel.isCollapsed) return;
      const text = sel.toString().trim();
      if (text.length < 3) return;
      const root = previewRef.current;
      if (!root) return;
      let node = sel.anchorNode;
      while (node && node !== root) node = node.parentNode;
      if (!node) return;
      setSelection(text.slice(0, 600));
    };
    document.addEventListener("mouseup", handleMouseUp);
    return () => document.removeEventListener("mouseup", handleMouseUp);
  }, [phase, fullPost]);

  const canGenerate = tema.trim().length > 0 && !!ctaId && !generating;

  const handleClear = () => {
    setTema("");
    setCtaId(null);
    setSelectedTopicChip(null);
    clearActivePost();
    setAktivPosztState(null);
  };

  const fillFromActivePost = () => {
    const p = getActivePost();
    if (!p) return;
    setTema(p.tema || "");
    setCtaId(p.cta_tipus || null);
    toast.success("Aktív poszt betöltve");
  };

  // Actual generation call. Always reachable directly; `handleGenerate` is
  // the user-facing trigger which may interpose the overwrite-warning dialog.
  const runGenerate = async () => {
    if (!canGenerate) return;
    setGenerating(true);
    setVazlatok([]);
    setFullPost("");
    setPostTitle("");
    setPostFirstLine("");
    setReelsContent(null);
    setCarouselContent(null);
    setChosenSketch(null);
    setLastSketchHookCats([]);
    try {
      const res = await api.post(apiPath("/sketches"), {
        voice_id: activeAlap.voice,
        client_id: activeAlap.client,
        offer_id: activeAlap.offer,
        tema: tema.trim(),
        cta_tipus: ctaId,
        aktiv_poszt: aktivPoszt || null,
        topic_object: selectedTopicObject || null,
      });
      const vs = Array.isArray(res.data?.vazlatok) ? res.data.vazlatok : [];
      if (!vs.length) {
        toast.error("Nem érkezett vázlat. Próbáld újra.");
        return;
      }
      setVazlatok(vs);
      const used = Array.isArray(res.data?.used_hook_categories) ? res.data.used_hook_categories : [];
      setLastSketchHookCats(used);
      setPhase("output");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "A generálás sikertelen volt.");
    } finally {
      setGenerating(false);
    }
  };

  // Sketch-phase chat: re-roll the 3 sketches with the user's instruction
  // injected as a free-form hint, excluding Hook-Bank categories already
  // used. Output-phase chat falls back to `requestRefine` (existing F3 flow).
  const regenerateSketches = async (instructionOverride) => {
    const utasitas = (instructionOverride || chatInput).trim();
    if (!utasitas) return;
    if (generating) return;
    setGenerating(true);
    setChatInput("");
    try {
      const res = await api.post(apiPath("/sketches"), {
        voice_id: activeAlap.voice,
        client_id: activeAlap.client,
        offer_id: activeAlap.offer,
        tema: tema.trim(),
        cta_tipus: ctaId,
        aktiv_poszt: aktivPoszt || null,
        topic_object: selectedTopicObject || null,
        excluded_hook_categories: lastSketchHookCats,
        user_instruction: utasitas,
      });
      const vs = Array.isArray(res.data?.vazlatok) ? res.data.vazlatok : [];
      if (vs.length) {
        setVazlatok(vs);
        setChosenSketch(null);
        const used = Array.isArray(res.data?.used_hook_categories) ? res.data.used_hook_categories : [];
        setLastSketchHookCats((prev) => Array.from(new Set([...(prev || []), ...used])));
        toast.success("Új vázlatok elkészültek.");
      } else {
        toast.error("Nem érkezett új vázlat.");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Új vázlatok generálása sikertelen.");
    } finally {
      setGenerating(false);
    }
  };

  // Unified chat-submit dispatcher: sketch phase → regenerateSketches,
  // output phase → requestRefine (existing F3 flow).
  const handleSubmitChat = async (instructionOverride) => {
    if (fullPost) {
      return requestRefine(instructionOverride);
    }
    return regenerateSketches(instructionOverride);
  };

  // User-facing trigger. If there's already a finished post on screen AND the
  // user hasn't opted out of the overwrite warning, open the confirmation
  // dialog; otherwise generate immediately.
  const handleGenerate = async () => {
    if (!canGenerate) return;
    if (fullPost && !overwriteWarningDisabled) {
      setOverwriteDialogOpen(true);
      return;
    }
    await runGenerate();
  };

  const handleOverwriteSave = async () => {
    try {
      await api.post(apiPath("/save"), {
        name: postTitle || null,
        tema: tema.trim(),
        cta_tipus: ctaId,
        valasztott_insight: chosenSketch
          ? {
              insight_cim: chosenSketch.insight_cim,
              horog: chosenSketch.horog,
              uj_nezopont: chosenSketch.uj_nezopont,
            }
          : null,
        teljes_poszt_szoveg: fullPost,
        ...(isReels && reelsContent ? { reels_content: reelsContent } : {}),
        ...(isCarousel && carouselContent ? { carousel_content: carouselContent } : {}),
        topic_object: selectedTopicObject || (chosenSketch && chosenSketch.topic_object) || null,
        chosen_internal: (chosenSketch && chosenSketch.internal) || null,
        topic_label: (chosenSketch && (chosenSketch.topic_label || chosenSketch.tema)) || "",
        angle_label: (chosenSketch && (chosenSketch.angle_label || chosenSketch.nezopont)) || "",
        post_title: postTitle || null,
        post_first_line: postFirstLine || null,
      });
      toast.success("Korábbi poszt mentve a Tudástárba.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Mentés sikertelen.");
      // Even on save failure, close the dialog and proceed — the user
      // explicitly opted to move on.
    }
    setOverwriteDialogOpen(false);
    await runGenerate();
  };

  const handleOverwriteDiscard = async () => {
    setOverwriteDialogOpen(false);
    await runGenerate();
  };

  const handleOverwriteDisableForever = async () => {
    setOverwriteWarningDisabled(true);
    try {
      await api.patch("/user/preferences", {
        preferences: { [OVERWRITE_PREF_KEY]: true },
      });
    } catch { /* silent: local state already updated */ }
  };

  const handleExpand = async (sketch) => {
    if (expanding) return;
    setExpanding(true);
    setChosenSketch(sketch);
    setSaved(false);
    try {
      const res = await api.post(apiPath("/full"), {
        voice_id: activeAlap.voice,
        client_id: activeAlap.client,
        offer_id: activeAlap.offer,
        tema: tema.trim(),
        cta_tipus: ctaId,
        valasztott_vazlat: sketch,
      });
      const poszt = res.data?.teljes_poszt || "";
      if (poszt) {
        setFullPost(poszt);
        setPostTitle((res.data?.post_title || "").trim());
        setPostFirstLine((res.data?.post_first_line || "").trim());
        if (res.data?.metadata) setPostMeta(res.data.metadata);
        // Reels: strukturált 9-mezős content átadása az UI-nak
        if (res.data?.reels_content && typeof res.data.reels_content === "object") {
          setReelsContent(res.data.reels_content);
        } else {
          setReelsContent(null);
        }
        // Carousel: strukturált dia-alapú content átadása az UI-nak
        if (res.data?.carousel_content && typeof res.data.carousel_content === "object") {
          setCarouselContent(res.data.carousel_content);
        } else {
          setCarouselContent(null);
        }
      } else toast.error("Üres válasz. Próbáld újra.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "A teljes poszt írása sikertelen volt.");
    } finally {
      setExpanding(false);
    }
  };

  // F3-style refine: kick off a new suggestion (loading → suggestion).
  // `quickButtonId` (optional) — ha 6 Quick Button-ból érkezett, a backend
  // a saját QUICK_BUTTON_INSTRUCTIONS dict-jéből veszi az utasítást
  // (single source of truth). A `utasitas` ekkor üres lehet.
  //
  // webinarOverride mód: a /refine helyett a webinar saját refine
  // endpointját hívjuk, a 4 input mezőt is átküldjük (a Writer prompt
  // konzisztens identitása miatt).
  const refineEndpoint = webinarOverride?.refinePath || apiPath("/refine");
  const refineExtraBody = webinarOverride?.userInputs
    ? {
        webinar_cim: webinarOverride.userInputs.webinar_cim || "",
        mit_tanuljon: webinarOverride.userInputs.mit_tanuljon || "",
        miert_te: webinarOverride.userInputs.miert_te || "",
        hova_hivjuk: webinarOverride.userInputs.hova_hivjuk || "",
      }
    : {};

  const requestRefine = async (instructionOverride, quickButtonId = null) => {
    const utasitas = (instructionOverride || chatInput).trim();
    if (!utasitas && !quickButtonId) return;
    if (!fullPost) return;
    if (refinePhase === "loading" || refinePhase === "suggestion") return;
    const quoteToSend = selection;
    setSuggestionError(false);
    setRefinePhase("loading");
    if (!instructionOverride) setChatInput("");
    try {
      const res = await api.post(refineEndpoint, {
        voice_id: activeAlap.voice,
        client_id: activeAlap.client,
        offer_id: activeAlap.offer,
        jelenlegi_poszt: fullPost,
        utasitas: utasitas || "",
        quote: quoteToSend || null,
        quick_button: quickButtonId || null,
        ...refineExtraBody,
      });
      const data = res.data || {};
      setSuggestion({
        mod: data.mod === "snippet" ? "snippet" : "teljes",
        snippet: data.snippet || "",
        teljes: data.teljes_poszt || "",
        instr: utasitas || (quickButtonId || ""),
        quote: quoteToSend || null,
      });
      setRefinePhase("suggestion");
    } catch (e) {
      setSuggestion({ mod: "error", instr: utasitas || (quickButtonId || ""), quote: quoteToSend || null });
      setSuggestionError(true);
      setRefinePhase("suggestion");
    }
  };

  // Re-run refinement with a "different variation" hint, F3 pattern.
  const retryVariant = async () => {
    if (!suggestion || regenerating) return;
    const previousSnippet =
      suggestion.mod === "snippet" ? suggestion.snippet : suggestion.teljes;
    setRegenerating(true);
    setSuggestionError(false);
    try {
      const res = await api.post(refineEndpoint, {
        voice_id: activeAlap.voice,
        client_id: activeAlap.client,
        offer_id: activeAlap.offer,
        jelenlegi_poszt: fullPost,
        utasitas:
          suggestion.instr +
          "\n\nGenerálj egy érdemben MÁSIK változatot, mint az előzőt." +
          (previousSnippet
            ? ` Az előző javaslat ez volt: "${previousSnippet.slice(0, 280)}". Kerüld el az ismétlést.`
            : ""),
        quote: suggestion.quote || null,
        ...refineExtraBody,
      });
      const data = res.data || {};
      setSuggestion((s) => ({
        ...s,
        snippet: data.snippet || "",
        teljes: data.teljes_poszt || "",
        mod: data.mod === "snippet" ? "snippet" : "teljes",
      }));
    } catch (e) {
      setSuggestionError(true);
    } finally {
      setRegenerating(false);
    }
  };

  const retryAfterError = () => {
    if (!suggestion) return;
    setSuggestionError(false);
    requestRefine(suggestion.instr);
  };

  const cancelSuggestion = () => {
    setSuggestion(null);
    setSuggestionError(false);
    setRefinePhase("idle");
  };

  const applySuggestion = () => {
    if (!suggestion) return;
    const prevPost = fullPost;
    let nextPost = fullPost;
    if (suggestion.mod === "snippet" && suggestion.snippet && suggestion.quote) {
      // Replace the first occurrence of the quote with the snippet.
      const idx = prevPost.indexOf(suggestion.quote);
      if (idx >= 0) {
        nextPost =
          prevPost.slice(0, idx) +
          suggestion.snippet +
          prevPost.slice(idx + suggestion.quote.length);
      } else {
        // Fallback: append snippet at end
        nextPost = prevPost + "\n\n" + suggestion.snippet;
      }
    } else if (suggestion.mod === "teljes" && suggestion.teljes) {
      nextPost = suggestion.teljes;
    } else {
      return;
    }
    setUndoSnapshot(prevPost);
    setFullPost(nextPost);
    setSelection(null);
    window.getSelection()?.removeAllRanges();
    // Also clear after React commits the new DOM, so any residual native
    // selection on the freshly-rendered nodes is dropped too.
    setTimeout(() => {
      try { window.getSelection()?.removeAllRanges(); } catch { /* noop */ }
    }, 0);
    setSuggestion(null);
    setSaved(false);
    setRefinePhase("applied");
    if (undoTimerRef.current) clearTimeout(undoTimerRef.current);
    undoTimerRef.current = setTimeout(() => {
      setRefinePhase("idle");
      setUndoSnapshot(null);
    }, 8000);
  };

  const undoApplied = () => {
    if (!undoSnapshot) return;
    setFullPost(undoSnapshot);
    setUndoSnapshot(null);
    setRefinePhase("idle");
    if (undoTimerRef.current) clearTimeout(undoTimerRef.current);
  };

  useEffect(() => () => {
    if (undoTimerRef.current) clearTimeout(undoTimerRef.current);
  }, []);

  const clearSelection = () => {
    setSelection(null);
    window.getSelection()?.removeAllRanges();
  };

  const handleCopy = async () => {
    if (!fullPost) return;
    try {
      await navigator.clipboard.writeText(fullPost);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
      toast.success("Vágólapra másolva");
    } catch {
      toast.error("Másolás nem sikerült");
    }
  };

  const handleDownload = () => {
    if (!fullPost) return;
    const blob = new Blob([fullPost], { type: "text/plain;charset=utf-8" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `${platform}-poszt-${(tema || "mex").toLowerCase().replace(/\s+/g, "-").slice(0, 40)}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(a.href);
  };

  const handleSave = async () => {
    if (!fullPost || saved || savingVault) return;
    setSavingVault(true);
    try {
      // webinarOverride mód: a webinar saját save endpointot hív, más
      // payload-shape-pel (4 input mező + carousel_content; nincs CTA, nincs
      // chosenSketch, nincs topic_object).
      if (webinarOverride?.savePath) {
        const ui = webinarOverride.userInputs || {};
        await api.post(webinarOverride.savePath, {
          voice_id: activeAlap.voice || null,
          client_id: activeAlap.client || null,
          offer_id: activeAlap.offer || null,
          name: postTitle || null,
          webinar_cim: ui.webinar_cim || tema.trim(),
          mit_tanuljon: ui.mit_tanuljon || "",
          miert_te: ui.miert_te || "",
          hova_hivjuk: ui.hova_hivjuk || "",
          teljes_poszt_szoveg: fullPost,
          carousel_content: carouselContent || null,
          post_title: postTitle || null,
          post_first_line: postFirstLine || null,
        });
        setSaved(true);
        toast.success("Mentve a Tudástárba");
        return;
      }
      const insight = chosenSketch
        ? { insight_cim: chosenSketch.insight_cim, horog: chosenSketch.horog, uj_nezopont: chosenSketch.uj_nezopont }
        : null;
      await api.post(apiPath("/save"), {
        name: postTitle || null,
        tema: tema.trim(),
        cta_tipus: ctaId,
        valasztott_insight: insight,
        teljes_poszt_szoveg: fullPost,
        ...(isReels && reelsContent ? { reels_content: reelsContent } : {}),
        ...(isCarousel && carouselContent ? { carousel_content: carouselContent } : {}),
        topic_object: selectedTopicObject || (chosenSketch && chosenSketch.topic_object) || null,
        chosen_internal: (chosenSketch && chosenSketch.internal) || null,
        topic_label: (chosenSketch && (chosenSketch.topic_label || chosenSketch.tema)) || "",
        angle_label: (chosenSketch && (chosenSketch.angle_label || chosenSketch.nezopont)) || "",
        post_title: postTitle || null,
        post_first_line: postFirstLine || null,
      });
      setSaved(true);
      toast.success("Mentve a Tudástárba");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Mentés sikertelen.");
    } finally {
      setSavingVault(false);
    }
  };

  // ─── Spin-off → Azonnal adaptált poszt a célplatformon ────────────
  // 2026-05-31 (revízió): a spin-off-pille AZONNAL kész adaptált posztot
  // generál a célplatformra (NEM a Beállításokra ugrat). A felhasználó
  // közvetlenül a célplatform poszt-megjelenítő oldalára érkezik a kész
  // tartalommal.
  //
  // Folyamat:
  //   1) A SOURCE wrapperből hívjuk a célplatform `/rewrite-from-source`
  //      endpointját. Backend visszaadja az adaptált posztot.
  //   2) Az eredmény + a setup-metadata (téma, vázlat, CTA) handoffba
  //      kerül a sessionStorage-en keresztül `phase: "output"` flaggel.
  //   3) `onSwitchPlatform(target)` remountolja a célplatform wrappert.
  //   4) A célwrapper mount-kor olvassa a handoffot, közvetlenül az
  //      output fázisba lép a kész adaptált poszttal.
  //   5) Ha innen a user a Beállításokra megy, a setup már elő van
  //      töltve (téma, vázlat, CTA), ahol mindent módosíthat — és onnan
  //      NULLÁRÓL új posztot generálhat.
  const HANDOFF_KEY = "magnetpost_spinoff_handoff";

  useEffect(() => {
    let raw;
    try { raw = sessionStorage.getItem(HANDOFF_KEY); } catch { return; }
    if (!raw) return;
    let h;
    try { h = JSON.parse(raw); } catch { sessionStorage.removeItem(HANDOFF_KEY); return; }
    if (!h || h.target !== platform) return;
    try { sessionStorage.removeItem(HANDOFF_KEY); } catch { /* ignore */ }
    // Setup-metadata előtöltése (akkor is hasznos, ha rögtön az output-on
    // van a felhasználó — a "Beállítások" gomb visszaviszi ide).
    if (h.tema) setTema(h.tema);
    if (h.ctaId) setCtaId(h.ctaId);
    if (h.selectedTopicChip) setSelectedTopicChip(h.selectedTopicChip);
    if (h.selectedTopicObject) setSelectedTopicObject(h.selectedTopicObject);
    if (h.chosenSketch) setChosenSketch(h.chosenSketch);
    if (Array.isArray(h.vazlatok)) setVazlatok(h.vazlatok);
    // Ha a handoff már tartalmazza a kész adaptált posztot, output-ra
    // ugrunk vele. Egyébként marad input fázisban (Beállítás-előtöltés).
    if (h.phase === "output" && h.fullPost) {
      setFullPost(h.fullPost);
      if (h.postTitle) setPostTitle(h.postTitle);
      if (h.postFirstLine) setPostFirstLine(h.postFirstLine);
      if (h.postMeta) setPostMeta(h.postMeta);
      if (h.reelsContent && typeof h.reelsContent === "object") setReelsContent(h.reelsContent);
      if (h.carouselContent && typeof h.carouselContent === "object") setCarouselContent(h.carouselContent);
      setPhase("output");
    } else {
      setPhase("input");
      const PLATFORM_NAMES = {
        facebook: "Facebook", linkedin: "LinkedIn",
        instagram_reels: "IG Reels", carousel: "Carousel",
      };
      toast.info(`Előtöltöttem a ${PLATFORM_NAMES[platform] || platform} beállításokat. Módosíthatsz, mielőtt generálsz.`);
    }
  // Mount-only effect — a platform a closure része, de nem változik.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Folyamatban lévő spin-off állapot (csak a UI-jelzéshez):
  const [rewritingTo, setRewritingTo] = useState(null);

  const handleSpinOff = async (target) => {
    if (target === platform) return;
    if (!fullPost) return;
    if (!onSwitchPlatform) {
      toast.error("Platform-váltó callback hiányzik.");
      return;
    }
    if (rewritingTo) return;
    setRewritingTo(target);
    const TARGET_LABELS = {
      facebook: "Facebook", linkedin: "LinkedIn",
      instagram_reels: "Reels", carousel: "Carousel",
    };
    const targetLabel = TARGET_LABELS[target] || target;
    const t = toast.loading(`${targetLabel} változat készítése…`);
    // Az új generikus `/rewrite-from-source` endpoint minden platformon
    // azonos sémát követ — egyetlen body, source_platform field.
    const TARGET_TO_PATH_SLUG = {
      facebook: "facebook", linkedin: "linkedin",
      instagram_reels: "reels", carousel: "carousel",
    };
    try {
      const res = await api.post(`/attraction/post-${TARGET_TO_PATH_SLUG[target]}/rewrite-from-source`, {
        voice_id: activeAlap.voice || null,
        client_id: activeAlap.client || null,
        offer_id: activeAlap.offer || null,
        source_platform: platform,
        forras_poszt: fullPost,
        tema: (chosenSketch?.topic_label || chosenSketch?.tema || tema || "").trim(),
        nezopont: (chosenSketch?.angle_label || chosenSketch?.nezopont || "").trim(),
        cta_tipus: ctaId,
        valasztott_vazlat: chosenSketch || null,
      });
      const newPoszt = res.data?.teljes_poszt || "";
      if (!newPoszt.trim()) throw new Error("Üres válasz.");
      // Handoff csomag: az adaptált poszt + a setup-metadata, hogy a
      // Beállítások gomb visszavezetése esetén minden elő legyen töltve.
      const handoff = {
        target,
        phase: "output",
        fullPost: newPoszt,
        postTitle: (res.data?.post_title || "").trim(),
        postFirstLine: (res.data?.post_first_line || "").trim(),
        postMeta: res.data?.metadata || null,
        reelsContent: res.data?.reels_content || null,
        carouselContent: res.data?.carousel_content || null,
        // Setup-metadata az átíráshoz (Beállítások előtöltése):
        tema: (tema || "").trim(),
        ctaId: ctaId || null,
        selectedTopicChip: selectedTopicChip || null,
        selectedTopicObject: selectedTopicObject || null,
        chosenSketch: chosenSketch || null,
        vazlatok: Array.isArray(vazlatok) ? vazlatok : [],
        source_platform: platform,
        timestamp: Date.now(),
      };
      try {
        sessionStorage.setItem(HANDOFF_KEY, JSON.stringify(handoff));
      } catch (e) {
        console.warn("Spin-off handoff sessionStorage failed:", e);
      }
      toast.dismiss(t);
      toast.success(`${targetLabel} változat elkészült.`);
      onSwitchPlatform(target);
    } catch (e) {
      toast.dismiss(t);
      toast.error(e?.response?.data?.detail || `A ${targetLabel} átírás sikertelen.`);
    } finally {
      setRewritingTo(null);
    }
  };

  // Disable scrolling outside this component for a full-page F3-feel.
  // ──────────────────────────────────────────────────────────────────────
  // Render: két útvonal az `inlineHost` prop alapján.
  //   • inlineHost=false (legacy): a teljes pane-t kitölti — header +
  //     AnimatePresence(input/output) — pontosan, mint eddig.
  //   • inlineHost=true: a komponens beágyazható a parent-shell-be
  //     (Email-mintát követve).
  //       - displayMode="inline": CSAK az InputView jelenik meg (shell és
  //         fejléc nélkül). A parent felül mutatja a kártya-gridet.
  //       - displayMode="full": a teljes PostHeader + Beállítás/Poszt tab
  //         + AnimatePresence shell, `height: 100%` flex-slotban (NEM
  //         `position: fixed`, NEM `100vh`) — a sidebar végig látszik.
  // ──────────────────────────────────────────────────────────────────────
  if (inlineHost && inlineHostDisplayMode === "inline") {
    return (
      <>
        <OverwriteWarningDialog
          open={overwriteDialogOpen}
          onSave={handleOverwriteSave}
          onDiscard={handleOverwriteDiscard}
          onClose={() => setOverwriteDialogOpen(false)}
          onDisableForever={handleOverwriteDisableForever}
        />
        <div className="w-full" data-testid="post-fb-shell-inline">
          <InputView
            platformLabel={PLATFORM_LABEL}
            platformTitle={PLATFORM_TITLE}
            isReels={isReels}
            platform={platform}
            tema={tema} setTema={setTema}
            ctaId={ctaId} setCtaId={setCtaId}
            topics={topics} topicsLoading={topicsLoading}
            topicObjects={topicObjects}
            topicsRegenerating={topicsRegenerating}
            onRegenerateTopics={regenerateTopics}
            selectedTopicChip={selectedTopicChip} setSelectedTopicChip={setSelectedTopicChip}
            setSelectedTopicObject={setSelectedTopicObject}
            aktivPoszt={aktivPoszt}
            fillFromActivePost={fillFromActivePost}
            handleClear={handleClear}
            canGenerate={canGenerate} generating={generating}
            handleGenerate={handleGenerate}
            inlineHost
          />
        </div>
      </>
    );
  }

  return (
    <div
      className="flex flex-col"
      style={
        inlineHost
          ? {
              // Email-mintát követve: a parent shell `<main>` content-területen
              // belül szervez egy 100vh konténert, ami a sidebar mellett tölti
              // ki a helyet. Itt csak kitöltjük a parent-flex-slotot — sem
              // `position: fixed`, sem `100vh`. A bal oldali fő menü végig
              // látszik. PostHeader + tabok + AnimatePresence chrome változatlan.
              height: "100%",
              minHeight: 0,
              flex: 1,
              background: "#0a0a12",
              overflow: "hidden",
            }
          : { height: "100vh", background: "#0a0a12", overflow: "hidden" }
      }
      data-testid="post-fb-shell"
    >
      <OverwriteWarningDialog
        open={overwriteDialogOpen}
        onSave={handleOverwriteSave}
        onDiscard={handleOverwriteDiscard}
        onClose={() => setOverwriteDialogOpen(false)}
        onDisableForever={handleOverwriteDisableForever}
      />
      <PostHeader
        onBackToOpening={onClose}
        activeTab={phase === "output" && (vazlatok.length || fullPost) ? "tab2" : "tab1"}
        onTabChange={(t) => {
          // webinarOverride mód: a Beállítás tab nem releváns (a setup
          // a parent WebinarAgent intake-jén történt), ezért a tabok
          // navigációját letiltjuk — a header tisztán dekoratív marad.
          if (webinarOverride) return;
          if (t === "tab1") setPhase("input");
          else if (t === "tab2" && (vazlatok.length || fullPost)) setPhase("output");
        }}
        tab2Disabled={!vazlatok.length && !fullPost}
        // Dinamikus fejléc-cím (B-verzió, 2026-05-31):
        //   - "Mágnesposzt Varázsló" a típusválasztón, téma-pille és
        //     CTA fázisban (mind setup, semmi vázlat még).
        //   - A platform neve, amint a 3 vázlatkártya megjelenik
        //     (platform-specifikus tartalom kezdődik), VAGY a kész
        //     poszt-megjelenítőn.
        // A `vazlatok.length > 0 || fullPost` feltétel mindkettőt
        // lefedi: a vázlat-fázist (output phase + nincs még fullPost
        // = a 3 vázlat látszik) és a poszt-megjelenítőt.
        headerTitle={
          webinarOverride?.headerTitle ||
          ((vazlatok.length > 0 || fullPost) ? PLATFORM_TITLE : "Mágnesposzt Varázsló")
        }
        tab2Label={webinarOverride?.tab2Label || "Poszt"}
      />

      <div
        className="relative flex-1 min-h-0"
        style={{ overflow: "hidden" }}
        data-testid="post-fb-body"
      >
        <AnimatePresence mode="wait" initial={false}>
          {phase === "input" ? (
            <motion.div
              key="input"
              initial={{ x: -30, opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              exit={{ x: 30, opacity: 0 }}
              transition={{ duration: 0.28, ease: [0.22, 0.9, 0.32, 1] }}
              className="absolute inset-0 overflow-y-auto"
            >
              <InputView
                platformLabel={PLATFORM_LABEL}
                platformTitle={PLATFORM_TITLE}
                isReels={isReels}
                platform={platform}
                tema={tema} setTema={setTema}
                ctaId={ctaId} setCtaId={setCtaId}
                topics={topics} topicsLoading={topicsLoading}
                topicObjects={topicObjects}
                topicsRegenerating={topicsRegenerating}
                onRegenerateTopics={regenerateTopics}
                selectedTopicChip={selectedTopicChip} setSelectedTopicChip={setSelectedTopicChip}
                setSelectedTopicObject={setSelectedTopicObject}
                aktivPoszt={aktivPoszt}
                fillFromActivePost={fillFromActivePost}
                handleClear={handleClear}
                canGenerate={canGenerate} generating={generating}
                handleGenerate={handleGenerate}
              />
            </motion.div>
          ) : (
            <motion.div
              key="output"
              initial={{ x: 30, opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              exit={{ x: -30, opacity: 0 }}
              transition={{ duration: 0.28, ease: [0.22, 0.9, 0.32, 1] }}
              className="absolute inset-0 flex flex-col"
            >
              <OutputView
                vazlatok={vazlatok}
                chosenSketch={chosenSketch}
                fullPost={fullPost}
                postMeta={postMeta}
                postTitle={postTitle}
                expanding={expanding}
                generating={generating}
                onExpand={handleExpand}
                selection={selection}
                clearSelection={clearSelection}
                chatInput={chatInput}
                setChatInput={setChatInput}
                refinePhase={refinePhase}
                suggestion={suggestion}
                suggestionError={suggestionError}
                regenerating={regenerating}
                undoSnapshot={undoSnapshot}
                onSubmitChat={handleSubmitChat}
                onQuickAction={(action) => requestRefine(action.instruction, action.id)}
                onApplySuggestion={applySuggestion}
                onRetryVariant={retryVariant}
                onRetryAfterError={retryAfterError}
                onCancelSuggestion={cancelSuggestion}
                onUndoApplied={undoApplied}
                onCopy={handleCopy} copied={copied}
                onDownload={handleDownload}
                onSave={handleSave} saved={saved} savingVault={savingVault}
                onSpinOff={handleSpinOff}
                previewRef={previewRef}
                platform={platform}
                reelsContent={reelsContent}
                carouselContent={carouselContent}
                webinarOverride={webinarOverride}
              />
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}

/* ─────────────────────────── F3-style header ─────────────────────────── */

export function PostHeader({ onBackToOpening, activeTab, onTabChange, tab2Disabled, headerTitle = "Mágnesposzt Varázsló", tab2Label = "Poszt" }) {
  const tab1Ref = useRef(null);
  const tab2Ref = useRef(null);
  const [dims, setDims] = useState({ left1: 0, w1: 0, left2: 0, w2: 0 });
  const recompute = () => {
    const t1 = tab1Ref.current, t2 = tab2Ref.current;
    if (!t1 || !t2) return;
    setDims({ left1: t1.offsetLeft, w1: t1.offsetWidth, left2: t2.offsetLeft, w2: t2.offsetWidth });
  };
  useLayoutEffect(() => { recompute(); }, [activeTab, tab2Disabled]);
  useEffect(() => {
    const r = () => recompute();
    window.addEventListener("resize", r);
    return () => window.removeEventListener("resize", r);
  }, []);
  const sliderLeft = activeTab === "tab1" ? dims.left1 : dims.left2;
  const sliderWidth = activeTab === "tab1" ? dims.w1 : dims.w2;

  return (
    <header
      className="sticky top-0 z-40 flex items-center justify-between gap-4"
      style={{ padding: "14px 22px", background: "#0d0d16", borderBottom: "1px solid rgba(255,255,255,0.05)" }}
      data-testid="post-fb-header"
    >
      <div className="flex items-center gap-3 min-w-0">
        <button
          type="button"
          onClick={onBackToOpening}
          className="inline-flex items-center gap-1.5 transition hover:text-white"
          style={{ fontSize: 12, color: "#7a7a95", background: "transparent", border: "none", cursor: "pointer", fontFamily: "inherit" }}
          data-testid="post-fb-back-to-opening"
        >
          <ArrowLeft size={13} weight="bold" /> Vissza
        </button>
        <span style={{ width: 1, height: 14, background: "rgba(255,255,255,0.10)" }} />
        <span
          aria-hidden="true"
          style={{
            width: 24, height: 24, borderRadius: 6,
            background: `linear-gradient(135deg, ${PURPLE_ACCENT}, #4e3fd8)`,
            display: "inline-flex", alignItems: "center", justifyContent: "center",
            boxShadow: `0 4px 14px -6px ${PURPLE_ACCENT}aa`,
          }}
        >
          <Sparkle size={13} weight="fill" color="white" />
        </span>
        <span className="truncate" style={{ fontSize: 14, color: "white", fontWeight: 500, letterSpacing: "-0.005em" }}>
          {headerTitle}
        </span>
      </div>

      <div
        style={{
          height: 38, background: "rgba(255,255,255,0.03)",
          border: "1px solid rgba(255,255,255,0.06)",
          borderRadius: 8, padding: 3,
          display: "flex", alignItems: "center", position: "relative",
        }}
      >
        <div
          aria-hidden="true"
          style={{
            position: "absolute", top: 3, bottom: 3,
            left: sliderLeft, width: sliderWidth,
            background: "rgba(124,108,242,0.18)",
            border: "1px solid rgba(124,108,242,0.28)",
            borderRadius: 6,
            transition: "left 0.22s ease-out, width 0.22s ease-out",
            zIndex: 1, pointerEvents: "none",
            opacity: sliderWidth > 0 ? 1 : 0,
          }}
        />
        <HeaderTab refEl={tab1Ref} active={activeTab === "tab1"} onClick={() => onTabChange?.("tab1")}>
          Beállítás
        </HeaderTab>
        <HeaderTab
          refEl={tab2Ref}
          active={activeTab === "tab2"}
          disabled={tab2Disabled}
          onClick={() => !tab2Disabled && onTabChange?.("tab2")}
          title={tab2Disabled ? "Először generálj" : undefined}
        >
          {tab2Label}
        </HeaderTab>
      </div>
    </header>
  );
}

function HeaderTab({ refEl, active, disabled, children, onClick, title }) {
  return (
    <button
      ref={refEl}
      type="button"
      onClick={onClick}
      disabled={disabled}
      title={title}
      style={{
        padding: "5px 14px", fontSize: 12, fontWeight: active ? 600 : 500,
        cursor: disabled ? "not-allowed" : "pointer",
        borderRadius: 6,
        display: "inline-flex", alignItems: "center", gap: 6,
        transition: "color 0.18s ease-out",
        zIndex: 2, position: "relative",
        background: "transparent", border: "1px solid transparent",
        color: active ? "white" : "#7a7a95",
        opacity: disabled ? 0.4 : 1,
        whiteSpace: "nowrap", fontFamily: "inherit",
      }}
    >
      {active && (
        <span
          aria-hidden="true"
          style={{
            width: 5, height: 5, borderRadius: "50%",
            background: PURPLE_ACCENT,
            boxShadow: "0 0 6px rgba(124,108,242,0.7)",
          }}
        />
      )}
      {children}
    </button>
  );
}

/* ─────────────────────────── INPUT VIEW ─────────────────────────── */

function InputView({
  platformLabel, platformTitle, isReels = false, platform = "facebook",
  tema, setTema, ctaId, setCtaId, topics, topicsLoading,
  topicObjects, setSelectedTopicObject,
  topicsRegenerating, onRegenerateTopics,
  selectedTopicChip, setSelectedTopicChip,
  aktivPoszt, fillFromActivePost, handleClear,
  canGenerate, generating, handleGenerate,
  inlineHost = false,
}) {
  return (
    <div className="px-6 md:px-10 py-10 max-w-[820px] mx-auto" data-testid="post-fb-input">
      {/* Inline-host módban a fejléc-blokk (eyebrow + cím + alcím + Törlés)
          NEM látszik — a fölötte kiemelt platform-kártya már jelzi a
          kiválasztást. Legacy fullscreen módban változatlanul látszik. */}
      {!inlineHost && (
        <>
          {/* Header strip */}
          <div
            style={{
              fontSize: 11, fontWeight: 600, color: TEAL,
              letterSpacing: "0.18em", textTransform: "uppercase", marginBottom: 12,
            }}
          >
            VONZÁS · 01 · POSZT · {platformLabel}
          </div>
          {/* Header row: cím + jobbra fent az oldalkezelő gombok */}
          <div className="flex items-start justify-between gap-6">
            <h1
              className="font-heading"
              style={{
                fontSize: 36, fontWeight: 600, letterSpacing: "-0.018em",
                color: "#e8e8f0", lineHeight: 1.15, marginBottom: 10,
                flex: 1,
              }}
            >
              {platformTitle}
            </h1>
            <div className="flex items-center gap-2 shrink-0" style={{ marginTop: 4 }}>
              <button
                type="button"
                onClick={handleClear}
                data-testid="post-fb-clear"
                style={{
                  fontSize: 12, padding: "7px 13px", borderRadius: 999,
                  background: "transparent", border: "1px solid rgba(255,255,255,0.10)",
                  color: "#9898ad", cursor: "pointer",
                  fontFamily: "inherit", fontWeight: 500,
                  display: "inline-flex", alignItems: "center", gap: 6,
                }}
              >
                <Eraser size={12} weight="bold" /> Törlés
              </button>
            </div>
          </div>
          <p style={{ fontSize: 14.5, color: "#9898ad", lineHeight: 1.6, maxWidth: 580 }}>
            Két kérdés, és írunk három vázlatot az aktív Márkahangod, Avatarod és Ajánlatod alapján.
          </p>
        </>
      )}

      {/* Q1 — extra felső térrel, hogy a fenti gombsor után legyen levegő */}
      <div className="mb-12" style={{ marginTop: inlineHost ? 0 : 56 }}>
        <label
          style={{
            fontSize: 11, color: TEAL, letterSpacing: "0.20em",
            textTransform: "uppercase", fontWeight: 600,
            display: "block", marginBottom: 18,
          }}
        >
          01 · MIRŐL SZÓLJON A POSZT?
        </label>
        {topicsLoading ? (
          <div className="flex items-center gap-2 text-[12.5px] text-[#9898ad] mb-4">
            <CircleNotch size={13} className="animate-spin" /> Téma-javaslatok generálása…
          </div>
        ) : topics.length > 0 ? (
          <>
            <div className="flex flex-wrap gap-2.5 mb-3" data-testid="post-fb-topic-chips">
              {topics.map((raw) => {
                const t = cleanTopic(raw);
                const active = selectedTopicChip === t;
                return (
                  <button
                    key={t}
                    type="button"
                    onClick={() => {
                      setSelectedTopicChip(t);
                      setTema(t);
                      // Find the full topic object behind this chip title
                      // so we can pass it to /sketches (Outline Generator v2).
                      const obj = (topicObjects || []).find((o) => (o?.title || "").trim() === t.trim());
                      setSelectedTopicObject(obj || null);
                    }}
                    data-testid={`post-fb-chip-${t.toLowerCase().replace(/\s+/g, "-").slice(0, 30)}`}
                    style={{
                      fontSize: 12.5, padding: "9px 15px", borderRadius: 999,
                      background: active ? TEAL_BG_ACTIVE : TEAL_BG,
                      border: `1px solid ${active ? TEAL_BORDER_ACTIVE : TEAL_BORDER}`,
                      color: active ? "#cef1fb" : TEAL,
                      cursor: "pointer", fontFamily: "inherit", fontWeight: 500,
                      transition: "all 0.16s",
                    }}
                  >
                    {t}
                  </button>
                );
              })}
            </div>
            <div className="flex justify-start mb-7" style={{ marginTop: 4 }}>
              <button
                type="button"
                onClick={onRegenerateTopics}
                disabled={topicsRegenerating}
                data-testid="post-fb-topics-regenerate"
                style={{
                  fontSize: 12, padding: "8px 16px", borderRadius: 999,
                  background: "transparent",
                  border: "1px dashed rgba(117,223,245,0.35)",
                  color: topicsRegenerating ? "#5a5a6a" : TEAL,
                  cursor: topicsRegenerating ? "wait" : "pointer",
                  fontFamily: "inherit", fontWeight: 500,
                  letterSpacing: "0.04em",
                  display: "inline-flex", alignItems: "center", gap: 6,
                  opacity: topicsRegenerating ? 0.7 : 1,
                  transition: "all 0.16s",
                }}
              >
                {topicsRegenerating ? (
                  <><CircleNotch size={12} className="animate-spin" /> Új témák…</>
                ) : (
                  <>↻ Még témákat</>
                )}
              </button>
            </div>
          </>
        ) : (
          // Empty-state fallback (post-2026-05-26): ha a /topics endpoint
          // semmit nem hozott vissza (LLM hiba / score-filter mind kiszűrte),
          // látható retry gomb jelenik meg ahelyett, hogy a felhasználó
          // azt érezze, hogy „eltűntek a téma-pillék". A textarea-ba
          // bármikor írhat saját témát.
          <div className="flex items-center justify-between gap-3 mb-7" data-testid="post-fb-topics-empty">
            <span className="text-[12.5px] text-[#9898ad]">
              Nem érkezett téma-javaslat. Próbáld újra, vagy írj saját témát alább.
            </span>
            <button
              type="button"
              onClick={onRegenerateTopics}
              disabled={topicsRegenerating}
              data-testid="post-fb-topics-retry"
              style={{
                fontSize: 12, padding: "8px 16px", borderRadius: 999,
                background: "transparent",
                border: "1px dashed rgba(117,223,245,0.35)",
                color: topicsRegenerating ? "#5a5a6a" : TEAL,
                cursor: topicsRegenerating ? "wait" : "pointer",
                fontFamily: "inherit", fontWeight: 500,
                letterSpacing: "0.04em",
                display: "inline-flex", alignItems: "center", gap: 6,
                opacity: topicsRegenerating ? 0.7 : 1,
                transition: "all 0.16s",
                whiteSpace: "nowrap",
              }}
            >
              {topicsRegenerating ? (
                <><CircleNotch size={12} className="animate-spin" /> Próbálom…</>
              ) : (
                <>↻ Új téma-pillék</>
              )}
            </button>
          </div>
        )}
        <textarea
          value={tema}
          onChange={(e) => { setTema(e.target.value); setSelectedTopicChip(null); setSelectedTopicObject(null); }}
          data-testid="post-fb-theme"
          placeholder="Vagy írj sajátot…"
          rows={2}
          style={{
            width: "100%", background: "rgba(255,255,255,0.025)",
            border: "1px solid rgba(255,255,255,0.08)", borderRadius: 12,
            padding: "14px 16px", fontSize: 14, color: "#e8e8f0",
            fontFamily: "inherit", outline: "none",
            resize: "vertical", lineHeight: 1.55,
          }}
        />
      </div>

      {/* Q2 — extra felső térrel, hogy a Q1 textarea és a Q2 cím
          között legyen levegő. */}
      <div className="mb-8" style={{ marginTop: 56 }}>
        <label
          style={{
            fontSize: 11, color: TEAL, letterSpacing: "0.20em",
            textTransform: "uppercase", fontWeight: 600,
            display: "block", marginBottom: 18,
          }}
        >
          02 · MI LEGYEN A POSZT VÉGÉN?
        </label>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3.5 items-stretch">
          {(
            platform === "linkedin" ? CTA_OPTIONS_LINKEDIN :
            platform === "carousel" ? CTA_OPTIONS_CAROUSEL :
            isReels ? CTA_OPTIONS_REELS :
            CTA_OPTIONS_DEFAULT
          ).map((opt) => {
            const active = ctaId === opt.id;
            return (
              <button
                key={opt.id}
                type="button"
                onClick={() => setCtaId(opt.id)}
                data-testid={`post-fb-cta-${opt.id}`}
                style={{
                  textAlign: "left",
                  background: active ? TEAL_BG_ACTIVE : "rgba(255,255,255,0.025)",
                  border: `1px solid ${active ? TEAL_BORDER_ACTIVE : "rgba(255,255,255,0.08)"}`,
                  borderRadius: 12, padding: "16px 18px",
                  cursor: "pointer", fontFamily: "inherit",
                  transition: "all 0.18s",
                  // Egyforma magasság minden képernyőméreten — a grid
                  // items-stretch + a button height:100% biztosítja, hogy
                  // a 3 CTA-opció mindig egy sorban, egyforma magasságban
                  // marad. (2026-05-26 fix az „Ingyenes tartalom lejjebb
                  // csúszik" laptop-bug ellen.)
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "flex-start",
                }}
              >
                <div style={{ fontSize: 13.5, fontWeight: 600, color: active ? "#cef1fb" : "#e8e8f0", marginBottom: 8 }}>
                  {opt.title}
                </div>
                <div style={{ fontSize: 12, color: "#9898ad", lineHeight: 1.5 }}>{opt.desc}</div>
              </button>
            );
          })}
        </div>
      </div>

      <div className="flex justify-center pb-4" style={{ marginTop: 56 }}>
        <motion.button
          whileHover={{ scale: !generating ? 1.02 : 1 }}
          whileTap={{ scale: !generating ? 0.98 : 1 }}
          onClick={handleGenerate}
          disabled={generating || !canGenerate}
          data-testid="post-fb-generate"
          className="inline-flex items-center justify-center"
          style={{
            // Generálás közben MARADJON lila (csak halványabb opacity),
            // NE váltson feketére. A `canGenerate=false` állapot szürke
            // marad (mert akkor a felhasználó még nem töltötte ki a
            // két kérdést).
            background: generating
              ? "linear-gradient(135deg, #7c6cf2, #5e4cd8)"
              : canGenerate
              ? "linear-gradient(135deg, #7c6cf2, #5e4cd8)"
              : "rgba(255,255,255,0.04)",
            color: generating ? "#ffffff" : canGenerate ? "white" : "#6a6a80",
            padding: "14px 24px",
            borderRadius: 12,
            fontSize: 15,
            fontWeight: 600,
            position: "relative",
            overflow: "hidden",
            boxShadow: generating
              ? "0 8px 28px rgba(124,108,242,0.30), 0 0 0 1px #7c6cf244, inset 0 1px 0 rgba(255,255,255,0.18)"
              : canGenerate
              ? "0 8px 28px rgba(124,108,242,0.35), 0 0 0 1px #7c6cf255, inset 0 1px 0 rgba(255,255,255,0.18)"
              : "none",
            border: generating || canGenerate ? "none" : "1px solid rgba(255,255,255,0.08)",
            cursor: generating ? "wait" : canGenerate ? "pointer" : "not-allowed",
            minWidth: 280,
            gap: 10,
            opacity: generating ? 0.78 : 1,
            fontFamily: "inherit",
          }}
        >
          <PostFbTopProgressBar visible={generating} />
          {generating ? (
            <CircleNotch size={16} weight="bold" className="animate-spin" />
          ) : (
            <Sparkle size={16} weight="fill" />
          )}
          {generating ? "Generálás folyamatban…" : "Mágnesposzt generálása"}
        </motion.button>
      </div>
    </div>
  );
}

/* ─────────────────────────── OUTPUT VIEW ─────────────────────────── */

function OutputView(props) {
  const {
    vazlatok, chosenSketch, fullPost, postMeta, postTitle, expanding, generating, onExpand,
    selection, clearSelection,
    chatInput, setChatInput,
    refinePhase, suggestion, suggestionError, regenerating, undoSnapshot,
    onSubmitChat, onQuickAction,
    onApplySuggestion, onRetryVariant, onRetryAfterError, onCancelSuggestion,
    onUndoApplied,
    onCopy, copied, onDownload,
    onSave, saved, savingVault,
    onSpinOff, previewRef,
    platform = "facebook",
    reelsContent = null,
    carouselContent = null,
    webinarOverride = null,
  } = props;

  const isMac = typeof navigator !== "undefined" && /Mac/i.test(navigator.platform);
  const inSketchPhase = !fullPost;
  const isLoading = refinePhase === "loading";
  const showSuggestion = refinePhase === "suggestion" && suggestion;
  const showApplied = refinePhase === "applied";
  // Sketch-phase: disable while regenerating sketches via the chat input.
  const sketchBusy = inSketchPhase && generating;
  const inputDisabled = isLoading || showSuggestion || sketchBusy;

  // webinarOverride mód: a 3 fix szöveget (leftTitle, leftBody, meta sor)
  // a wrapper adja át. Default módban (`webinarOverride === null`) az
  // eredeti Carousel + MagnetPost copy marad bit-azonosan érintetlen.
  const defaultLeftTitle = inSketchPhase ? "Elkészültek a vázlatok" : "Elkészült a poszt.";
  const defaultLeftBody = inSketchPhase
    ? "Olvasd át a 3 javaslatot jobbra. Kattints arra amelyiket teljes poszttá szeretnéd írni, vagy írj lent a chat ablakban, ha további változatokat szeretnél létrehozni."
    : "Olvasd át a jobb oldalon. Ha minden rendben, mentsd el a Tudástárba. Ha valamin finomítanál, jelölj ki egy részt a posztban, vagy írd lent, mit változtassak.";
  const leftTitle = webinarOverride?.customLeftTitle || defaultLeftTitle;
  const leftBody = webinarOverride?.customLeftBody || defaultLeftBody;
  const placeholder = selection
    ? "Mit változtassak a kijelölt részen?"
    : inSketchPhase
      ? "Írd ide, ha más szögű vázlatokat szeretnél."
      : "Mit szeretnél finomítani?";

  // Snippet shown by SuggestionCard depends on mod.
  const suggestionSnippet = suggestion
    ? (suggestion.mod === "snippet" ? suggestion.snippet : suggestion.teljes)
    : "";

  // Amikor a kész poszt megérkezik (vázlat → teljes poszt átmenet), a jobb
  // oldali cream-card scroll containerét AZONNAL a tetejére állítjuk
  // (nincs smooth animáció), hogy a felhasználó rögtön a gombsort, a
  // post_title-t, a meta sort és a poszt első sorát lássa.
  // useLayoutEffect: a scroll pozíció már a render commit előtt 0 lesz,
  // így nincs látható mozgás.
  useLayoutEffect(() => {
    if (!fullPost) return;
    const el = previewRef?.current;
    if (!el) return;
    let scrollEl = el.parentElement;
    while (scrollEl && scrollEl.scrollHeight <= scrollEl.clientHeight) {
      scrollEl = scrollEl.parentElement;
      if (!scrollEl || scrollEl === document.body) break;
    }
    if (scrollEl) {
      scrollEl.scrollTop = 0;
    }
  }, [fullPost, previewRef]);

  return (
    <div className="flex flex-col h-full" style={{ background: "#0a0a12" }}>
      <div className="grid grid-cols-1 lg:grid-cols-[440px_1fr] flex-1 min-h-0 overflow-hidden">

        {/* LEFT — Finomító Panel (F3-identical structure) */}
        <div
          className="flex flex-col border-r min-h-0 h-full"
          style={{ borderColor: "rgba(255,255,255,0.04)" }}
          data-testid="post-fb-finomito"
        >
          <div className="flex-1 overflow-y-auto" style={{ padding: "32px 28px 14px" }}>
            <div style={{ maxWidth: 480 }}>
              <div
                className="font-heading"
                style={{
                  fontSize: 28,
                  fontWeight: 600,
                  lineHeight: 1.3,
                  letterSpacing: "-0.015em",
                  color: "#ffffff",
                  marginBottom: 18,
                }}
              >
                {leftTitle}
              </div>
              <div
                style={{
                  fontSize: 17,
                  fontWeight: 400,
                  lineHeight: 1.7,
                  color: "#c0c0d0",
                }}
              >
                {leftBody}
              </div>
            </div>

            {/* Selection workspace — F3 pattern: KIJELÖLT RÉSZ + chips OR loading OR suggestion */}
            <AnimatePresence>
              {!inSketchPhase && (selection || isLoading || showSuggestion) && (
                <motion.div
                  key="workspace"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.2 }}
                  style={{
                    background: "rgba(124,108,242,0.025)",
                    border: "1px solid rgba(124,108,242,0.10)",
                    borderRadius: 12, padding: 14, margin: "32px -8px 0",
                  }}
                  data-testid="post-fb-selection-workspace"
                >
                  {/* Selection card */}
                  {(selection || (suggestion && suggestion.quote)) && (
                    <div
                      style={{
                        background: "rgba(255,255,255,0.04)",
                        borderLeft: "2px solid rgba(124,108,242,0.40)",
                        borderRadius: 8, padding: "11px 13px", marginBottom: 12,
                      }}
                    >
                      <div className="flex items-start gap-2.5">
                        <div className="flex-1 min-w-0">
                          <div
                            style={{
                              fontSize: 9, textTransform: "uppercase", letterSpacing: "0.16em",
                              color: "#8a8a9c", fontWeight: 600, marginBottom: 4,
                            }}
                          >
                            KIJELÖLT RÉSZ
                          </div>
                          <div
                            style={{
                              fontSize: 12.5, color: "#c0c0d0", lineHeight: 1.5,
                              display: "-webkit-box", WebkitLineClamp: 3,
                              WebkitBoxOrient: "vertical", overflow: "hidden",
                            }}
                          >
                            {(() => {
                              const q = selection || suggestion?.quote || "";
                              return `„${q.length > 240 ? q.slice(0, 240) + "…" : q}"`;
                            })()}
                          </div>
                        </div>
                        {!showSuggestion && !isLoading && (
                          <button
                            type="button"
                            onClick={clearSelection}
                            aria-label="Kijelölés törlése"
                            style={{
                              width: 22, height: 22, borderRadius: 5,
                              background: "rgba(255,255,255,0.04)",
                              border: "1px solid rgba(255,255,255,0.08)",
                              color: "#9898ad", cursor: "pointer",
                              display: "inline-flex", alignItems: "center", justifyContent: "center",
                              flexShrink: 0,
                            }}
                            data-testid="post-fb-selection-clear"
                          >
                            <XIcon size={11} weight="bold" />
                          </button>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Quick chips: only in idle phase with a selection */}
                  {!isLoading && !showSuggestion && selection && (
                    <div
                      className="grid grid-cols-2 sm:grid-cols-3 gap-2.5"
                      data-testid="post-fb-quick-actions"
                    >
                      {QUICK_REFINE_ACTIONS.map((qa) => (
                        <button
                          key={qa.id}
                          type="button"
                          onClick={() => onQuickAction(qa)}
                          data-testid={`post-fb-quick-${qa.id}`}
                          style={{
                            fontSize: 13, padding: "10px 14px", borderRadius: 999,
                            background: "rgba(124,108,242,0.10)",
                            border: "1px solid rgba(124,108,242,0.28)",
                            color: "#cfc8ff", cursor: "pointer",
                            fontFamily: "inherit", fontWeight: 500,
                            whiteSpace: "nowrap",
                            textAlign: "center",
                            width: "100%",
                          }}
                        >
                          {qa.label}
                        </button>
                      ))}
                    </div>
                  )}

                  {/* Loading state — F3 "Finomítás folyamatban…" */}
                  {isLoading && (
                    <div
                      className="flex items-center gap-2.5"
                      style={{ fontSize: 13, color: "#a8a8c0", padding: "4px 2px" }}
                      data-testid="post-fb-refine-loading"
                    >
                      <CircleNotch size={14} weight="bold" className="animate-spin" />
                      Finomítás folyamatban…
                    </div>
                  )}

                  {/* Suggestion card — F3 pattern reused 1:1 */}
                  {showSuggestion && (
                    <div data-testid="post-fb-suggestion">
                      <SuggestionCard
                        snippet={suggestionSnippet}
                        isError={suggestionError}
                        regenerating={regenerating}
                        onApply={onApplySuggestion}
                        onRetryVariant={onRetryVariant}
                        onRetryAfterError={onRetryAfterError}
                        onCancel={onCancelSuggestion}
                      />
                    </div>
                  )}
                </motion.div>
              )}
            </AnimatePresence>

            {/* Applied block (transient, like F3) */}
            <AnimatePresence>
              {showApplied && (
                <motion.div
                  key="applied"
                  initial={{ opacity: 0, y: 6 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.18 }}
                  style={{ margin: "24px 0 0" }}
                  data-testid="post-fb-applied"
                >
                  <AppliedBlock onUndo={onUndoApplied} canUndo={!!undoSnapshot} />
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {/* Bottom: F3-style chat input */}
          <form
            onSubmit={(e) => {
              e.preventDefault();
              if (chatInput.trim() && !inputDisabled) onSubmitChat();
            }}
            style={{ padding: "12px 24px 24px" }}
            data-testid="post-fb-finomito-form"
          >
            <div
              style={{
                background: "linear-gradient(180deg, rgba(243,238,228,0.06) 0%, rgba(243,238,228,0.02) 100%)",
                border: "1px solid rgba(243,238,228,0.14)",
                borderRadius: 12,
                padding: "5px 5px 5px 14px",
                display: "flex", alignItems: "center", gap: 10,
                boxShadow: "0 4px 16px rgba(0,0,0,0.25), inset 0 1px 0 rgba(255,255,255,0.06)",
                opacity: inputDisabled ? 0.5 : 1,
                pointerEvents: inputDisabled ? "none" : "auto",
              }}
            >
              <button
                type="button"
                aria-label="Csatolás"
                style={{
                  background: "transparent", border: "none", padding: 0,
                  color: "#8a8a9c", cursor: "pointer",
                  display: "inline-flex", alignItems: "center", justifyContent: "center",
                }}
              >
                <Paperclip size={14} weight="regular" />
              </button>
              <input
                value={chatInput}
                onChange={(e) => setChatInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
                    e.preventDefault();
                    if (chatInput.trim() && !inputDisabled) onSubmitChat();
                  }
                }}
                placeholder={placeholder}
                data-testid="post-fb-chat-input"
                disabled={inputDisabled}
                className="flex-1 outline-none"
                style={{
                  background: "transparent", border: "none", color: "white",
                  fontSize: 14, padding: "8px 0", fontFamily: "inherit",
                }}
              />
              <button
                type="submit"
                disabled={!chatInput.trim() || inputDisabled}
                data-testid="post-fb-chat-send"
                aria-label="Küldés"
                style={{
                  width: 36, height: 36,
                  background: "linear-gradient(135deg, #a89bff, #7c6cf2)",
                  color: "white", borderRadius: 9, border: "none",
                  display: "flex", alignItems: "center", justifyContent: "center",
                  cursor: chatInput.trim() && !inputDisabled ? "pointer" : "not-allowed",
                  opacity: chatInput.trim() && !inputDisabled ? 1 : 0.5,
                  boxShadow: chatInput.trim() && !inputDisabled
                    ? "0 4px 12px rgba(124,108,242,0.45), inset 0 0 0 1px rgba(255,255,255,0.10)"
                    : "none",
                }}
              >
                {(isLoading || sketchBusy) ? (
                  <CircleNotch size={15} weight="bold" className="animate-spin" />
                ) : (
                  <PaperPlaneTilt size={15} weight="fill" />
                )}
              </button>
            </div>
            <div
              className="flex justify-between"
              style={{ marginTop: 8, fontSize: 10.5, color: "#5a5a72" }}
            >
              <span>
                {inSketchPhase
                  ? 'Pl.: „adj 3 vázlatot, ami metaforából indul" vagy „más szögből".'
                  : "Tipp: jelölj ki egy részt jobboldalt - a kérésed arra fog vonatkozni."}
              </span>
              <span>{isMac ? "⌘" : "Ctrl"} + Enter</span>
            </div>
          </form>
        </div>

        {/* RIGHT — cream blueprint card */}
        <div
          className="overflow-y-auto h-full"
          style={{ background: "#0a0a12", padding: "28px 36px 56px 36px" }}
        >
          <div
            ref={previewRef}
            data-preview-theme="cream"
            data-testid="post-fb-cream-card"
            className="mr-auto cream-selectable"
            style={{
              background: "#f3eee4",
              color: "#0a0a12",
              borderRadius: 10,
              boxShadow: "0 16px 50px -22px rgba(0,0,0,0.50), 0 0 0 1px rgba(255,255,255,0.03)",
              padding: "32px 44px 56px",
              maxWidth: 860,
              marginLeft: 0,
              position: "relative",
            }}
          >
            {!inSketchPhase && (
              <div
                className="flex justify-end items-center"
                style={{ gap: 6, marginBottom: 28, position: "relative", zIndex: 1 }}
                data-html2canvas-ignore="true"
              >
                <CreamActionPill
                  onClick={onCopy}
                  icon={copied ? CheckCircle : CopyIcon}
                  label={copied ? "Másolva" : "Másolás"}
                  testid="post-fb-copy"
                  disabled={!fullPost}
                />
                <CreamActionPill
                  onClick={onDownload}
                  icon={DownloadSimple}
                  label="Letöltés"
                  testid="post-fb-download"
                  disabled={!fullPost}
                />
                <CreamActionPill
                  onClick={onSave}
                  icon={saved ? CheckCircle : DownloadSimple}
                  label={savingVault ? "Mentés…" : saved ? "Mentve" : "Mentés a Tudástárba"}
                  testid="post-fb-save"
                  disabled={!fullPost}
                  active={saved}
                />
              </div>
            )}

            <AnimatePresence mode="wait">
              {inSketchPhase ? (
                <motion.div
                  key="sketches"
                  initial={{ opacity: 0, y: 6 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.22 }}
                >
                  <h2
                    className="font-heading"
                    style={{
                      fontSize: 26, fontWeight: 700, color: "#0a0a12",
                      letterSpacing: "-0.018em", marginBottom: 10, lineHeight: 1.2,
                    }}
                  >
                    Posztvázlatok
                  </h2>
                  <p style={{ fontSize: 14, color: "#6a6a78", lineHeight: 1.6, marginBottom: 22, fontWeight: 400 }}>
                    Három különböző irányból építettünk vázlatot ehhez a témához. Válaszd ki, melyiket szeretnéd teljes posztá írni. Kattints a kártyára.
                  </p>
                  <div data-testid="post-fb-sketches">
                    {vazlatok.map((v, idx) => (
                      <CreamSketchSection
                        key={v.id}
                        sketch={v}
                        index={idx}
                        isLast={idx === vazlatok.length - 1}
                        expanding={expanding && chosenSketch?.id === v.id}
                        onClick={() => onExpand(v)}
                      />
                    ))}
                  </div>
                </motion.div>
              ) : (
                <motion.div
                  key="full"
                  initial={{ opacity: 0, y: 6 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.22 }}
                >
                  {postMeta && (postTitle || postMeta.tema || postMeta.cta_tipus) && (
                    <div
                      style={{
                        marginTop: 48,
                        marginBottom: 36,
                        paddingBottom: 2,
                        borderBottom: "0.5px solid #26A9C9",
                      }}
                      data-testid="post-metadata"
                    >
                      <div
                        style={{
                          display: "flex",
                          alignItems: "baseline",
                          justifyContent: "space-between",
                          gap: 16,
                          marginBottom: 26,
                        }}
                      >
                        <div
                          style={{
                            fontSize: 28, fontWeight: 700, color: "#0a0a12",
                            letterSpacing: "-0.014em", lineHeight: 1.2,
                            flex: 1, minWidth: 0,
                          }}
                          data-testid="post-title-h1"
                        >
                          {postTitle || postMeta.tema || "Mágnesposzt"}
                        </div>
                        {/* Reels-specifikus: a videó ajánlott hossza a CÍM
                            SORÁBA, jobbra igazítva (egy magasságba a cím
                            vízszintes vonalával). Csak Reels output-on és
                            csak ha a Writer kiadta a hossz mezőt. */}
                        {platform === "instagram_reels" && reelsContent?.ajanlott_hossz_mp && (
                          <div
                            data-testid="reels-length-badge"
                            style={{
                              fontSize: 13, fontWeight: 700,
                              color: "#135b6c",
                              letterSpacing: "0.01em",
                              padding: "6px 14px",
                              borderRadius: 999,
                              background: "rgba(20,142,175,0.10)",
                              border: "1px solid rgba(20,142,175,0.28)",
                              whiteSpace: "nowrap",
                              flexShrink: 0,
                            }}
                          >
                            <span style={{ opacity: 0.72, fontWeight: 600 }}>Hossz:</span>{" "}
                            {reelsContent.ajanlott_hossz_mp}
                          </div>
                        )}
                        {/* Carousel-specifikus length-badge eltávolítva
                            (2026-05-28) — a `Hossz: X dia` pill már nem
                            szerepel a cím sorában; csak a meta-sor
                            mutatja a TÉMA · NÉZŐPONT · CTA hármast. */}
                      </div>
                      <div style={{
                        fontSize: 10,
                        color: "#26A9C9",
                        lineHeight: 1.6,
                        display: "flex",
                        flexWrap: "nowrap",
                        gap: "2px 4px",
                        // Desktop: mindig egy soros meta sor (TÉMA · NÉZŐPONT · CTA).
                        // A CTA-t a backend hosszú címkéjéből rövidítjük
                        // (META_CTA_SHORT), így a sor minden Avatar/Téma
                        // hosszra kifér.
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        // Kicsit kompaktabb a vázlat 0.20em-jéhez képest,
                        // hogy a 3 meta adat (TÉMA · NÉZŐPONT · CTA) mindig
                        // egy sorba férjen ki a poszt-fejlécben.
                        letterSpacing: "0.14em",
                        fontWeight: 600,
                        fontStyle: "normal",
                        textTransform: "uppercase",
                      }}>
                        {webinarOverride?.customMetaRow ? (
                          webinarOverride.customMetaRow
                        ) : (
                          <>
                        {postMeta.tema && (
                          <span style={{ whiteSpace: "nowrap" }}>
                            TÉMA: <span style={{ fontWeight: 600 }}>{postMeta.tema}</span>
                          </span>
                        )}
                        {postMeta.tema && postMeta.nezopont && (
                          <span style={{
                            color: "#26A9C9",
                            padding: "0 5px",
                            display: "inline-block",
                            transform: "translateY(-1px)",
                            fontWeight: 700,
                          }}>·</span>
                        )}
                        {postMeta.nezopont && (
                          <span style={{ whiteSpace: "nowrap" }}>
                            NÉZŐPONT: <span style={{ fontWeight: 600 }}>{postMeta.nezopont}</span>
                          </span>
                        )}
                        {postMeta.nezopont && postMeta.cta_tipus && (
                          <span style={{
                            color: "#26A9C9",
                            padding: "0 5px",
                            display: "inline-block",
                            transform: "translateY(-1px)",
                            fontWeight: 700,
                          }}>·</span>
                        )}
                        {postMeta.cta_tipus && (
                          <span style={{ whiteSpace: "nowrap" }}>
                            CTA: <span style={{ fontWeight: 600 }}>{shortCta(postMeta.cta_tipus)}</span>
                          </span>
                        )}
                          </>
                        )}
                      </div>
                    </div>
                  )}
                  {platform === "instagram_reels" && reelsContent ? (
                    <ReelsContentView content={reelsContent} fallbackText={fullPost} />
                  ) : platform === "carousel" && carouselContent ? (
                    <CarouselContentView
                      content={carouselContent}
                      fallbackText={fullPost}
                      ctaTipus={postMeta?.cta_tipus || ""}
                      webinarMode={!!webinarOverride}
                    />
                  ) : (
                    <div
                      style={{
                        whiteSpace: "pre-wrap",
                        fontSize: 16, lineHeight: 1.75, color: "#1a1a22",
                        fontFamily: "Inter, ui-sans-serif, sans-serif",
                      }}
                    >
                      {fullPost}
                    </div>
                  )}
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {fullPost && !webinarOverride && (
            <div className="mr-auto" style={{ maxWidth: 860, marginLeft: 0, marginTop: 22, paddingBottom: 6 }}>
              <div className="text-[12.5px] text-[#9898ad] mb-3">
                Készíts ebből más formátumot is:
              </div>
              <div className="flex flex-wrap gap-2">
                {[
                  { label: "Facebook", id: "facebook" },
                  { label: "LinkedIn", id: "linkedin" },
                  { label: "IG Reels", id: "instagram_reels" },
                  { label: "IG Carousel", id: "carousel" },
                ].filter(({ id }) => id !== platform).map(({ label, id }) => (
                  <button
                    key={id}
                    type="button"
                    onClick={() => onSpinOff(id)}
                    data-testid={`post-fb-spinoff-${id}`}
                    style={{
                      fontSize: 12.5, padding: "8px 14px", borderRadius: 999,
                      background: TEAL_BG, border: `1px solid ${TEAL_BORDER}`,
                      color: TEAL, cursor: "pointer",
                      fontFamily: "inherit", fontWeight: 500,
                    }}
                    title={`Ugrás a ${label} beállításokra a jelenlegi setup-pal előre kitöltve`}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/* ─────────────────────────── Subcomponents ─────────────────────────── */

function CreamActionPill({ onClick, icon: Icon, label, testid, active, disabled, accent }) {
  // Default to the Attraction layer cyan accent. Each layer can pass its own
  // hex via `accent` to keep cream-card actions in-sync with the layer color.
  const accentColor = accent || TEAL;
  const accentBg = active ? "rgba(29,158,117,0.10)" : `${accentColor}14`;
  const accentBorder = active ? "rgba(29,158,117,0.30)" : `${accentColor}3d`;
  const accentText = active ? "#1d9e75" : "#135b6c";
  return (
    <button
      type="button"
      onClick={disabled ? undefined : onClick}
      data-testid={testid}
      disabled={disabled}
      style={{
        display: "inline-flex", alignItems: "center", gap: 6,
        padding: "6px 12px",
        background: accentBg,
        border: `1px solid ${accentBorder}`,
        borderRadius: 999,
        color: accentText,
        fontSize: 12, fontWeight: 500,
        cursor: disabled ? "not-allowed" : "pointer",
        opacity: disabled ? 0.4 : 1,
        whiteSpace: "nowrap",
        fontFamily: "inherit",
        transition: "all 180ms ease",
      }}
    >
      <Icon size={13} weight="regular" /> {label}
    </button>
  );
}

export function CreamSketchSection({ sketch, index, isLast, expanding, onClick }) {
  const [hover, setHover] = useState(false);
  // Cream akcent — Vonzás layer egységes #26A9C9 (--accent-blue).
  const ACCENT = "#26A9C9"; // kék
  return (
    <div
      onClick={expanding ? undefined : onClick}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      data-testid={`post-fb-sketch-${sketch.id}`}
      style={{
        cursor: expanding ? "wait" : "pointer",
        padding: "18px 0",
        borderBottom: isLast ? "none" : "1px solid rgba(10,10,18,0.08)",
        background: hover ? "rgba(38, 169, 201,0.06)" : "transparent",
        margin: hover ? "0 -16px" : "0",
        paddingLeft: hover ? 16 : 0,
        paddingRight: hover ? 16 : 0,
        borderRadius: hover ? 8 : 0,
        transition: "background 180ms ease",
      }}
    >
      <div
        style={{
          fontSize: 11,
          // Brand akcent kék (telítettebb, élénkebb Layer kék) — a meta
          // adatoknak külön akcent színe van, hogy elkülönüljön a TEAL
          // (#75dff5) layer-eyebrow-tól. Ez az élénk középkék pontosan
          // megegyezik az F3 szinteken használt brand-blue akcenttel.
          color: "#26A9C9",
          lineHeight: 1.6,
          marginBottom: 12,
          display: "flex", flexWrap: "wrap", gap: "2px 4px",
          letterSpacing: "0.20em",
          fontWeight: 600,
          textTransform: "uppercase",
        }}
        data-testid={`sketch-eyebrow-${index}`}
      >
        {sketch.tema && (
          <span>
            TÉMA: <span style={{ fontWeight: 600 }}>{sketch.tema}</span>
          </span>
        )}
        {(sketch.nezopont || sketch.szog) && sketch.tema && (
          <span style={{
            color: "#26A9C9",
            padding: "0 7px",
            display: "inline-block",
            transform: "translateY(-1px)",
            fontWeight: 700,
          }}>·</span>
        )}
        {(sketch.nezopont || sketch.szog) && (
          <span>
            NÉZŐPONT: <span style={{ fontWeight: 600 }}>{sketch.nezopont || sketch.szog}</span>
          </span>
        )}
      </div>
      <h3
        className="font-heading"
        style={{
          fontSize: 19, fontWeight: 700, color: "#0a0a12",
          lineHeight: 1.3, marginBottom: 8, letterSpacing: "-0.012em",
        }}
        data-testid={`sketch-horog-${index}`}
      >
        {sketch.horog}
      </h3>
      {(sketch.preview || sketch.uj_nezopont) && (
        <p
          style={{
            fontSize: 14, color: "#4a4a55", lineHeight: 1.55, marginBottom: 10,
            display: "-webkit-box", WebkitLineClamp: 3, WebkitBoxOrient: "vertical", overflow: "hidden",
            fontWeight: 400,
          }}
          data-testid={`sketch-preview-${index}`}
        >
          {sketch.preview || sketch.uj_nezopont}
        </p>
      )}
      <div
        style={{
          fontSize: 12.5, color: ACCENT, fontWeight: 600,
          display: "inline-flex", alignItems: "center", gap: 4,
        }}
      >
        {expanding ? (
          <>
            <CircleNotch size={12} weight="bold" className="animate-spin" /> Kibontás…
          </>
        ) : (
          <>Ezt írd meg teljesen →</>
        )}
      </div>
    </div>
  );
}


/* TopProgressBar — same as Avatar Express generate button.
   Inline-shimmer overlay that runs while generating=true.
*/
function PostFbTopProgressBar({ visible }) {
  if (!visible) return null;
  return (
    <div
      data-testid="post-fb-generate-progress"
      style={{
        position: "absolute",
        top: 0,
        left: 0,
        right: 0,
        height: 3,
        background: "rgba(255,255,255,0.18)",
        overflow: "hidden",
        pointerEvents: "none",
        zIndex: 2,
      }}
    >
      <motion.div
        style={{
          position: "absolute",
          top: 0,
          bottom: 0,
          width: "40%",
          background:
            "linear-gradient(90deg, transparent 0%, rgba(255,255,255,1) 50%, transparent 100%)",
        }}
        animate={{ left: ["-40%", "100%"] }}
        transition={{ duration: 1.2, repeat: Infinity, ease: "linear" }}
      />
    </div>
  );
}

// ─── ReelsContentView (v3) ────────────────────────────────────────────
// A Reels Writer v3 strukturált kimenete:
//   tipus + hossz badge
//   NYITOTT szekciók (alapból láthatóak, Másolás gombbal):
//     HOROG / ELHANGZÓ SZÖVEG / KÉPERNYŐSZÖVEG KLIPRŐL KLIPRE / LEÍRÁS
//   ÖSSZECSUKHATÓ szekciók (alapból zárva):
//     VIZUÁLIS UTASÍTÁS / HANG / MIÉRT MŰKÖDIK / ALTERNATÍV HORGOK
// Az „Elsődleges cél" badge ELTÁVOLÍTVA (duplikálta a CTA típust).

function ReelsBadge({ label, value }) {
  if (!value) return null;
  return (
    <div style={{
      display: "inline-flex", alignItems: "center", gap: 6,
      padding: "6px 11px", borderRadius: 999,
      background: "rgba(20,142,175,0.08)",
      border: "1px solid rgba(20,142,175,0.20)",
      fontSize: 11, color: "#138eaf", fontWeight: 600,
      letterSpacing: "0.04em",
    }}>
      <span style={{ opacity: 0.7 }}>{label}:</span>
      <span style={{ fontWeight: 700, color: "#135b6c" }}>{value}</span>
    </div>
  );
}

function ReelsCopyButton({ value, label, testid }) {
  return (
    <button
      type="button"
      onClick={() => {
        if (!value) return;
        const txt = typeof value === "function" ? value() : value;
        navigator.clipboard?.writeText(txt || "");
        toast.success(`${label} másolva.`);
      }}
      data-testid={testid}
      style={{
        fontSize: 11, fontWeight: 600,
        padding: "4px 10px", borderRadius: 999,
        background: "rgba(20,142,175,0.08)",
        border: "1px solid rgba(20,142,175,0.22)",
        color: "#138eaf", cursor: "pointer",
        fontFamily: "inherit",
      }}
    >
      Másolás
    </button>
  );
}

function ReelsOpenBlock({ icon, title, copyValue, copyLabel, copyTestid, testid, children, titleStyle }) {
  return (
    <div data-testid={testid} style={{ marginBottom: 24 }}>
      <div style={{
        display: "flex", alignItems: "center", justifyContent: "space-between",
        gap: 12, marginBottom: 10,
      }}>
        <div style={{
          // 2026-05-29: nagyobb szakasz-címke (F3-szint), Layer-kéken.
          // A strukturális szakasz-címek (HOROG / ELHANGZÓ SZÖVEG /
          // KÉPERNYŐSZÖVEG / LEÍRÁS stb.) most már CÍMszerű súllyal
          // jelennek meg, NEM meta-méretű apró betűvel.
          fontSize: 13.5, color: "#138eaf", fontWeight: 600,
          letterSpacing: "0.08em", textTransform: "uppercase",
        }}>
          {icon} {title}
        </div>
        {copyValue && (
          <ReelsCopyButton value={copyValue} label={copyLabel} testid={copyTestid} />
        )}
      </div>
      <div style={titleStyle || {
        fontSize: 15, color: "#1a1a22", lineHeight: 1.75,
        whiteSpace: "pre-wrap",
      }}>
        {children}
      </div>
    </div>
  );
}

function ReelsSection({ icon, title, children, defaultOpen = false, testid }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div
      data-testid={testid}
      style={{
        borderTop: "1px solid rgba(20,142,175,0.18)",
        paddingTop: 14, paddingBottom: 14, marginTop: 4,
      }}
    >
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        style={{
          width: "100%", display: "flex", alignItems: "center",
          gap: 10, background: "transparent", border: "none",
          padding: "4px 0", cursor: "pointer", textAlign: "left",
          fontFamily: "inherit",
        }}
      >
        <span style={{ fontSize: 16 }}>{icon}</span>
        <span style={{
          flex: 1, fontSize: 13, fontWeight: 700, color: "#0a0a12",
          letterSpacing: "0.04em", textTransform: "uppercase",
        }}>
          {title}
        </span>
        <span style={{
          fontSize: 12, color: "#138eaf", fontWeight: 700,
          transform: open ? "rotate(90deg)" : "rotate(0deg)",
          transition: "transform 0.18s ease",
        }}>›</span>
      </button>
      {open && (
        <div style={{
          marginTop: 12, fontSize: 14.5, lineHeight: 1.7, color: "#1a1a22",
          whiteSpace: "pre-wrap",
        }}>
          {children}
        </div>
      )}
    </div>
  );
}

function ReelsContentView({ content, fallbackText }) {
  if (!content || typeof content !== "object") {
    return (
      <div style={{
        whiteSpace: "pre-wrap", fontSize: 16, lineHeight: 1.75, color: "#1a1a22",
      }}>
        {fallbackText || ""}
      </div>
    );
  }
  const {
    horog_kepernyoszovege, vizualis_utasitas, elhangzo_szoveg,
    kepernyoszoveg_klipek, hang_utasitas, leiras,
    miert_mukodik, alternativ_horogok,
  } = content;
  const clips = Array.isArray(kepernyoszoveg_klipek) ? kepernyoszoveg_klipek : [];
  const alts = Array.isArray(alternativ_horogok) ? alternativ_horogok : [];

  return (
    <div data-testid="reels-content-view">
      {/* A Típus + Hossz badge-ek átkerültek a poszt-fejlécbe:
          - A NÉZŐPONT a meta-soron már megjeleníti a típust (a 8 kanonikus
            közül), így a Típus badge duplikáció lenne.
          - A Hossz a CÍM SORÁBA kerül, jobbra igazítva (OutputView-ban
            renderelt).
          A 4 nyitott szekciónál (HOROG / ELHANGZÓ SZÖVEG / KÉPERNYŐSZÖVEG /
          LEÍRÁS) NINCS külön "Másolás" gomb — a tetején lévő fő „Másolás"
          pille a teljes tartalomcsomagot másolja. Szakaszt kijelöléssel +
          billentyűkombinációval másol a felhasználó. */}

      {/* NYITOTT — HOROG */}
      {horog_kepernyoszovege && (
        <ReelsOpenBlock
          icon="📱"
          title="Horog (képernyőszöveg)"
          testid="reels-open-hook"
          titleStyle={{
            fontSize: 20, fontWeight: 600, color: "#0a0a12",
            lineHeight: 1.4, letterSpacing: "-0.008em",
          }}
        >
          <span data-testid="reels-hook">{horog_kepernyoszovege}</span>
        </ReelsOpenBlock>
      )}

      {/* NYITOTT — ELHANGZÓ SZÖVEG */}
      {elhangzo_szoveg && (
        <ReelsOpenBlock
          icon="🎙️"
          title="Elhangzó szöveg"
          testid="reels-open-voiceover"
        >
          <span data-testid="reels-voiceover">{elhangzo_szoveg}</span>
        </ReelsOpenBlock>
      )}

      {/* NYITOTT — KÉPERNYŐSZÖVEG KLIPRŐL KLIPRE */}
      {clips.length > 0 && (
        <ReelsOpenBlock
          icon="📝"
          title="Képernyőszöveg klipről klipre"
          testid="reels-open-clips"
        >
          {clips.map((c, i) => (
            <div key={i} style={{ marginBottom: 6 }}>
              <span style={{ fontWeight: 700, color: "#138eaf" }}>
                {c.klip_szam || i + 1}.
              </span>{" "}
              {c.szoveg}
            </div>
          ))}
        </ReelsOpenBlock>
      )}

      {/* NYITOTT — LEÍRÁS */}
      {leiras && (
        <ReelsOpenBlock
          icon="📄"
          title="Leírás (caption)"
          testid="reels-open-caption"
        >
          {leiras}
        </ReelsOpenBlock>
      )}

      {/* ÖSSZECSUKHATÓ szekciók — alapból zárva */}
      <div style={{ marginTop: 24 }}>
        {vizualis_utasitas && (
          <ReelsSection icon="🎥" title="Vizuális utasítás" testid="reels-section-visual">
            {vizualis_utasitas}
          </ReelsSection>
        )}
        {hang_utasitas && (
          <ReelsSection icon="🎵" title="Hang" testid="reels-section-audio">
            {hang_utasitas}
          </ReelsSection>
        )}
        {miert_mukodik && (
          <ReelsSection icon="💡" title="Miért működik" testid="reels-section-why">
            {miert_mukodik}
          </ReelsSection>
        )}
        {alts.length > 0 && (
          <ReelsSection icon="🔁" title="Alternatív horgok" testid="reels-section-alts">
            {alts.map((a, i) => (
              <div key={i} style={{ marginBottom: 6 }}>
                <span style={{ fontWeight: 700, color: "#138eaf" }}>{i + 1}.</span> {a}
              </div>
            ))}
          </ReelsSection>
        )}
      </div>
    </div>
  );
}


// ─── CarouselContentView ──────────────────────────────────────────────
// A Carousel Writer dia-alapú strukturált kimenete:
//   diak[]: 1-X. dia (HOOK / BEVONÁS / 1-4 BUILD / PAYOFF / INVITE)
//   leiras / miert_mukodik / alternativ_horgok[]
//
// Vizuális paletta: Layer-kék (#26A9C9 / #4FC1DC / #75dff5).
// Minden dia EGY BLOKK 3 részből:
//   (1) PILL A KÁRTYA FÖLÖTT (jobbra) — „N · SZAKASZ" (pl. „1 · HOROG").
//       Gyártói tájékozódást segíti, a végső dián NEM jelenik meg.
//   (2) A KÁRTYA — fehér, finom borderrel. CSAK az éles szöveg: cim
//       (kiemelt) + szoveg (törzs + opc. „Lapozz →" átvezető).
//       Meta-címke SOHA nincs itt.
//   (3) GRAFIKA — összecsukható. Halvány kép-ikon a kártya jobb alsó
//       sarkában. Kattintásra a grafikai instrukció nyílik meg a kártyán
//       belül halvány háttéren.
//
// A 8. (INVITE) dián, ha a CTA „Ingyenes tartalom" vagy „Jelentkezés
// ajánlatra", a kártya alján Layer-kék pill gomb jelenik meg a CTA
// nevével + illő ikonnal. „Beszélgetésindító" esetén nincs gomb.

const LAYER_BLUE = "#26A9C9";
const LAYER_BLUE_LIGHT = "#4FC1DC";
const LAYER_BLUE_BG = "rgba(38, 169, 201, 0.14)";

// CTA pill konfiguráció a 8. (INVITE) diához. „Beszélgetésindító"
// (CTE) esetén bewusst nincs pill — a kérdés MAGA a meghívás.
const CTA_PILL_CONFIG = {
  "Ingyenes tartalom":     { label: "Ingyenes tartalom",     Icon: Gift },
  "Jelentkezés ajánlatra": { label: "Jelentkezés ajánlatra", Icon: Rocket },
};

function CarouselContentView({ content, fallbackText, ctaTipus = "", webinarMode = false }) {
  // Mely diák grafika-blokkja van NYITVA (dia_szam alapján). Alapból
  // mind zárva. Per-dia toggle a kép-ikon kattintásra.
  const [openGrafikak, setOpenGrafikak] = useState({});
  if (!content || typeof content !== "object") {
    return (
      <div style={{
        whiteSpace: "pre-wrap", fontSize: 16, lineHeight: 1.75, color: "#1a1a22",
      }}>
        {fallbackText || ""}
      </div>
    );
  }
  const {
    diak, leiras, miert_mukodik, alternativ_horgok,
  } = content;
  const slides = Array.isArray(diak) ? diak : [];
  const alts = Array.isArray(alternativ_horgok) ? alternativ_horgok : [];
  const ctaPill = CTA_PILL_CONFIG[ctaTipus] || null;

  const toggleGrafika = (key) => {
    setOpenGrafikak(prev => ({ ...prev, [key]: !prev[key] }));
  };

  return (
    <div data-testid="carousel-content-view">
      {/* DIÁK szekció-felirat */}
      <div style={{
        fontSize: 11, color: LAYER_BLUE, fontWeight: 700,
        letterSpacing: "0.14em", textTransform: "uppercase",
        marginBottom: 14,
      }}>
        Diák
      </div>

      {/* Dia-blokkok: PILL (kártyán kívül) + KÁRTYA + lenyitható grafika */}
      {slides.map((dia, i) => {
        const num = dia?.dia_szam || (i + 1);
        const kategoria = (dia?.kategoria || "").trim();
        const cim = (dia?.cim || "").trim();
        const szoveg = (dia?.szoveg || "").trim();
        const grafika = (dia?.grafika || "").trim();
        const atvezeto = (dia?.atvezeto || "").trim();
        const isFirst = i === 0;
        const isLast = i === slides.length - 1;
        // Régi formátum kompat: ha cim === kategoria (a parser így
        // állítja be backward-compat módban) → ne mutassuk duplán.
        const hasDistinctCim = cim && cim !== kategoria;
        // INVITE pill csak az UTOLSÓ dián, és csak ha CTA típus megfelelő.
        const showInviteCta = isLast && ctaPill;
        const grafikaOpen = !!openGrafikak[num];

        // ── webinarMode (v6) ALATERNATÍV RENDER ───────────────────────
        // A Carousel és a Mágnesposzt flow bit-azonos marad (webinarMode
        // default `false`). Ha `true`, a slide v6 mezőit használjuk:
        //   - pill (magyar)
        //   - visible_headline (EGY cím, NEM dupla)
        //   - visible_bullets / visible_question / visible_cta
        //   - „Részletek" toggle → visual_suggestion + speaker_notes
        if (webinarMode) {
          const pill = (dia?.pill || kategoria || "").trim();
          const vh = (dia?.visible_headline || cim || "").trim();
          const bullets = Array.isArray(dia?.visible_bullets) ? dia.visible_bullets : [];
          const vq = (dia?.visible_question || "").trim();
          const vcta = (dia?.visible_cta || "").trim();
          const visual = (dia?.visual_suggestion || "").trim();
          const speaker = (dia?.speaker_notes || "").trim();
          const hasDetails = visual || speaker;
          const detailsOpen = !!openGrafikak[num];
          return (
            <div key={i} style={{ marginBottom: 18 }}>
              {/* pill — kártyán KÍVÜL, jobbra (mint a Carouselnél) */}
              <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 8 }}>
                <span style={{
                  fontSize: 13, color: LAYER_BLUE, fontWeight: 600,
                  letterSpacing: "0.08em", textTransform: "uppercase",
                  background: LAYER_BLUE_BG,
                  padding: "6px 14px", borderRadius: 999,
                }}>
                  {num}{pill ? ` · ${pill}` : ""}
                </span>
              </div>
              {/* a kártya */}
              <div
                data-testid={`carousel-dia-${num}`}
                style={{
                  position: "relative",
                  padding: "22px 26px 24px",
                  borderRadius: 12,
                  background: "#ffffff",
                  border: "0.5px solid rgba(20, 30, 40, 0.08)",
                }}
              >
                {/* EGY visible_headline — fő cím */}
                {vh && (
                  <div data-testid={`carousel-dia-${num}-headline`} style={{
                    fontSize: isFirst ? 22 : 19,
                    fontWeight: 600,
                    color: "#0a0a12",
                    lineHeight: 1.32,
                    letterSpacing: "-0.008em",
                    marginBottom: (bullets.length || vq || vcta) ? 14 : 0,
                    paddingRight: 90, // ne lógjon a Részletek-gomb alá
                  }}>
                    {vh}
                  </div>
                )}
                {/* visible_bullets — vizuálisan ELKÜLÖNÜLVE a címtől */}
                {bullets.length > 0 && (
                  <ul data-testid={`carousel-dia-${num}-bullets`} style={{
                    listStyle: "none", padding: 0, margin: 0,
                    display: "flex", flexDirection: "column", gap: 6,
                  }}>
                    {bullets.map((b, bi) => (
                      <li key={bi} style={{
                        fontSize: 14.5, color: "#3a4654", fontWeight: 400,
                        lineHeight: 1.55, paddingLeft: 18, position: "relative",
                      }}>
                        <span style={{
                          position: "absolute", left: 0, top: 6,
                          width: 6, height: 6, borderRadius: "50%",
                          background: LAYER_BLUE,
                        }} />
                        {b}
                      </li>
                    ))}
                  </ul>
                )}
                {/* visible_question (CSAK ha bullets nincs vagy plusz) */}
                {vq && (
                  <div style={{
                    marginTop: bullets.length ? 12 : 0,
                    fontSize: 15, color: LAYER_BLUE, fontWeight: 500,
                    lineHeight: 1.5, fontStyle: "italic",
                  }}>
                    {vq}
                  </div>
                )}
                {/* visible_cta */}
                {vcta && (
                  <div style={{
                    marginTop: bullets.length || vq ? 14 : 6,
                    display: "inline-flex", alignItems: "center",
                    background: LAYER_BLUE, color: "#fff",
                    fontSize: 13, fontWeight: 600,
                    padding: "8px 16px", borderRadius: 999,
                  }}>
                    {vcta}
                  </div>
                )}
                {/* RÉSZLETEK gomb a jobb alsó sarokban — lenyitja:
                    Vizuális javaslat + Előadói jegyzet (KÉT blokk). */}
                {hasDetails && (
                  <>
                    <button
                      type="button"
                      onClick={() => toggleGrafika(num)}
                      data-testid={`carousel-dia-${num}-details-toggle`}
                      aria-label={detailsOpen ? "Részletek bezárása" : "Részletek megnyitása"}
                      style={{
                        position: "absolute",
                        right: 12, bottom: 10,
                        background: detailsOpen ? LAYER_BLUE_BG : "transparent",
                        border: `1px solid ${detailsOpen ? LAYER_BLUE : "rgba(20, 30, 40, 0.10)"}`,
                        cursor: "pointer",
                        padding: "5px 12px", borderRadius: 999,
                        color: detailsOpen ? LAYER_BLUE : "#5b6975",
                        fontSize: 11.5, fontWeight: 600, letterSpacing: "0.04em",
                        textTransform: "uppercase",
                        fontFamily: "inherit",
                        transition: "color 0.15s, background 0.15s, border 0.15s",
                      }}
                    >
                      Részletek
                    </button>
                    {detailsOpen && (
                      <div
                        data-testid={`carousel-dia-${num}-details-body`}
                        style={{ marginTop: 18, display: "flex", flexDirection: "column", gap: 12 }}
                      >
                        {visual && (
                          <div style={{
                            padding: "12px 14px",
                            background: "rgba(20, 30, 40, 0.035)",
                            border: "0.5px solid rgba(20, 30, 40, 0.06)",
                            borderRadius: 8,
                            fontSize: 13, color: "#3a4654",
                            lineHeight: 1.6,
                          }}>
                            <div style={{
                              color: LAYER_BLUE, fontWeight: 700,
                              fontSize: 10.5, letterSpacing: "0.10em",
                              textTransform: "uppercase",
                              marginBottom: 6,
                            }}>
                              Vizuális javaslat
                            </div>
                            <div style={{ whiteSpace: "pre-wrap" }}>{visual}</div>
                          </div>
                        )}
                        {speaker && (
                          <div style={{
                            padding: "12px 14px",
                            background: "rgba(20, 30, 40, 0.035)",
                            border: "0.5px solid rgba(20, 30, 40, 0.06)",
                            borderRadius: 8,
                            fontSize: 13, color: "#3a4654",
                            lineHeight: 1.65,
                          }}>
                            <div style={{
                              color: LAYER_BLUE, fontWeight: 700,
                              fontSize: 10.5, letterSpacing: "0.10em",
                              textTransform: "uppercase",
                              marginBottom: 6,
                            }}>
                              Előadói jegyzet
                            </div>
                            <div style={{ whiteSpace: "pre-wrap" }}>{speaker}</div>
                          </div>
                        )}
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
          );
        }

        return (
          <div key={i} style={{ marginBottom: 18 }}>
            {/* (1) PILL A KÁRTYA FÖLÖTT, JOBBRA — „N · SZAKASZ" */}
            <div style={{
              display: "flex", justifyContent: "flex-end",
              marginBottom: 8,
            }}>
              <span style={{
                // 2026-05-29: nagyobb szakasz-címke (F3-szint), Layer-kéken.
                // A „N · SZAKASZ" pill címszerű súllyal, NEM meta-méretű
                // apró betűvel.
                fontSize: 13, color: LAYER_BLUE, fontWeight: 600,
                letterSpacing: "0.08em", textTransform: "uppercase",
                background: LAYER_BLUE_BG,
                padding: "6px 14px", borderRadius: 999,
              }}>
                {num}{kategoria ? ` · ${kategoria}` : ""}
              </span>
            </div>

            {/* (2) A KÁRTYA — fehér, finom border, CSAK éles szöveg */}
            <div
              data-testid={`carousel-dia-${num}`}
              style={{
                position: "relative",
                padding: "20px 24px",
                paddingBottom: 22,
                borderRadius: 12,
                background: "#ffffff",
                border: "0.5px solid rgba(20, 30, 40, 0.08)",
              }}
            >
              {hasDistinctCim && (
                <div style={{
                  fontSize: isFirst ? 20 : 18,
                  fontWeight: 500,
                  color: "#0a0a12",
                  lineHeight: 1.35,
                  letterSpacing: "-0.008em",
                  marginBottom: (szoveg || atvezeto) ? 8 : 0,
                  paddingRight: 28, // ne lógjon a grafika-ikon alá
                }}>
                  {cim}
                </div>
              )}
              {(szoveg || atvezeto) && (
                <div style={{
                  fontSize: isFirst && !hasDistinctCim ? 19 : 14,
                  fontWeight: isFirst && !hasDistinctCim ? 600 : 400,
                  color: hasDistinctCim ? "#3a4654" : "#0a0a12",
                  lineHeight: 1.7,
                  whiteSpace: "pre-wrap",
                  paddingRight: 28,
                }}>
                  {szoveg}
                  {atvezeto && (
                    <span style={{
                      color: LAYER_BLUE, fontWeight: 600,
                      marginLeft: szoveg ? 6 : 0,
                    }}>
                      {atvezeto}
                    </span>
                  )}
                </div>
              )}

              {/* INVITE CTA pill (csak utolsó dián, releváns CTA-knál) */}
              {showInviteCta && (
                <div style={{
                  marginTop: 14,
                  display: "flex", justifyContent: "flex-start",
                }}>
                  <span
                    data-testid="carousel-invite-cta-pill"
                    style={{
                      display: "inline-flex", alignItems: "center", gap: 8,
                      padding: "8px 16px",
                      borderRadius: 999,
                      background: LAYER_BLUE,
                      color: "#ffffff",
                      fontSize: 13, fontWeight: 600,
                      letterSpacing: "0.01em",
                    }}
                  >
                    <ctaPill.Icon size={15} weight="bold" />
                    {ctaPill.label}
                  </span>
                </div>
              )}

              {/* (3) GRAFIKA: halvány kép-ikon a jobb alsó sarokban,
                  kattintásra a grafika instrukció kinyílik a kártyán belül */}
              {grafika && (
                <>
                  <button
                    type="button"
                    onClick={() => toggleGrafika(num)}
                    data-testid={`carousel-dia-${num}-grafika-toggle`}
                    aria-label={grafikaOpen ? "Grafika instrukció bezárása" : "Grafika instrukció megnyitása"}
                    style={{
                      position: "absolute",
                      right: 12, bottom: 10,
                      background: "transparent",
                      border: "none",
                      cursor: "pointer",
                      padding: 4,
                      borderRadius: 6,
                      color: grafikaOpen ? LAYER_BLUE : "#9aa4ad",
                      opacity: grafikaOpen ? 1 : 0.5,
                      display: "inline-flex", alignItems: "center",
                      transition: "color 0.15s, opacity 0.15s",
                    }}
                    onMouseEnter={(e) => { e.currentTarget.style.opacity = "1"; }}
                    onMouseLeave={(e) => { e.currentTarget.style.opacity = grafikaOpen ? "1" : "0.5"; }}
                  >
                    <ImageIcon size={15} weight="regular" />
                  </button>
                  {grafikaOpen && (
                    <div
                      data-testid={`carousel-dia-${num}-grafika-body`}
                      style={{
                        marginTop: 14,
                        padding: "10px 14px",
                        background: "rgba(20, 30, 40, 0.035)",
                        border: "0.5px solid rgba(20, 30, 40, 0.06)",
                        borderRadius: 8,
                        fontSize: 12.5, color: "#5b6975",
                        fontStyle: "italic",
                        lineHeight: 1.6,
                        whiteSpace: "pre-wrap",
                      }}>
                      <span style={{
                        color: LAYER_BLUE, fontWeight: 700, fontStyle: "normal",
                        marginRight: 4,
                      }}>
                        Grafika:
                      </span>
                      {grafika}
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        );
      })}

      {/* LEÍRÁS (CAPTION) — nyitott szekció, saját Másolás gombbal */}
      {leiras && (
        <div style={{ marginTop: 28, marginBottom: 22 }}>
          <div style={{
            display: "flex", alignItems: "center",
            justifyContent: "space-between", marginBottom: 10,
          }}>
            <div style={{
              fontSize: 11, color: LAYER_BLUE, fontWeight: 700,
              letterSpacing: "0.14em", textTransform: "uppercase",
            }}>
              Leírás (caption)
            </div>
            <button
              type="button"
              onClick={() => {
                navigator.clipboard?.writeText(leiras);
                toast.success("Leírás másolva.");
              }}
              data-testid="carousel-copy-caption"
              style={{
                fontSize: 11, fontWeight: 600,
                padding: "4px 10px", borderRadius: 999,
                background: LAYER_BLUE_BG,
                border: `1px solid ${LAYER_BLUE_LIGHT}`,
                color: LAYER_BLUE, cursor: "pointer",
                fontFamily: "inherit",
              }}
            >
              Másolás
            </button>
          </div>
          <div style={{
            padding: "16px 20px",
            background: "#ffffff",
            border: "0.5px solid rgba(20, 30, 40, 0.08)",
            borderRadius: 12,
            fontSize: 14.5, color: "#1a1a22", lineHeight: 1.75,
            whiteSpace: "pre-wrap",
          }}>
            {leiras}
          </div>
        </div>
      )}

      {/* Összecsukható szekciók — mind ALAPBÓL ZÁRVA.
          A „VIZUÁLIS UTASÍTÁS" külön szekció MEGSZŰNT — a grafikai
          instrukciók mostantól diánként, a kártyán belül lenyithatók. */}
      <div style={{ marginTop: 24 }}>
        {miert_mukodik && (
          <ReelsSection icon="💡" title="Miért működik" testid="carousel-section-why">
            {miert_mukodik}
          </ReelsSection>
        )}
        {alts.length > 0 && (
          <ReelsSection icon="🔁" title="Alternatív horgok" testid="carousel-section-alts">
            {alts.map((a, i) => (
              <div key={i} style={{ marginBottom: 6 }}>
                <span style={{ fontWeight: 700, color: LAYER_BLUE }}>{i + 1}.</span> {a}
              </div>
            ))}
          </ReelsSection>
        )}
      </div>
    </div>
  );
}



